<template>
  <div class="wizard-root" :class="{ 'section-editor': isSectionEditor }">
    <SiteHeader v-if="!isSectionEditor" :user-email="userEmail" @logout="$emit('logout')" />

    <div class="wizard-body">
      <!-- Stepper (not in the portal's section editor, which shows a single step) -->
      <div v-if="!isSectionEditor" class="stepper">
        <div class="stepper-track">
          <div
            v-for="(st, idx) in stepMeta"
            :key="idx"
            class="stepper-item"
            :class="{ clickable: !done }"
            @click="goToStep(idx)"
          >
            <div
              class="stepper-circle"
              :class="{
                complete: idx < step || (done && idx === step),
                active: idx === step && !done,
              }"
            >
              <span v-if="idx < step || (done && idx === step)">&#10003;</span>
              <span v-else>{{ idx + 1 }}</span>
            </div>
            <div class="stepper-label" :class="{ active: idx === step }">
              {{ st }}
            </div>
          </div>
        </div>
        <div class="stepper-progress">
          <div class="stepper-progress-fill" :style="{ width: pct + '%' }"></div>
        </div>
      </div>

      <!-- Disables every field when reviewing an already-submitted application (see readOnlyMode). -->
      <fieldset :disabled="readOnlyMode" class="wizard-fieldset">
      <!-- STEP 0: Datos personales, edited in place in the portal: laid out like the section's summary. -->
      <div v-if="step === 0" class="step-panel pf-step">
        <section class="pf-group">
          <h3 class="pf-group-title">Identidad</h3>

          <div class="pf-photo" :class="{ 'pf-invalid': showStepError && !form.image_url }">
            <div class="pf-photo-head">
              <div v-drag-over class="pf-photo-frame" :class="{ uploading: uploadingKeys.image_url }">
                <img v-if="avatarPreviewUrl" :src="avatarPreviewUrl" alt="Foto de perfil" class="pf-photo-img" />
                <PortalIcon v-else name="user" :size="16" />
                <!-- Covers the whole frame, so a photo dropped on it is taken too. -->
                <input id="avatar-file" type="file" accept="image/*" aria-label="Foto de perfil" @change="onAvatarChange" />
              </div>
              <div class="pf-photo-body">
                <div class="pf-photo-title">Foto de perfil</div>
                <div class="pf-photo-text">Use una foto reciente, de frente.</div>
              </div>
            </div>
            <div class="pf-actions">
              <label for="avatar-file" class="pf-btn">
                <PortalIcon name="upload" :size="14" />
                <span>{{ uploadingKeys.image_url ? "Subiendo…" : form.image_url ? "Cambiar foto" : "Subir foto" }}</span>
              </label>
              <button v-if="form.image_url" type="button" class="pf-btn-link" @click="setVal('image_url', null)">
                Quitar
              </button>
            </div>
            <div v-if="showStepError && !form.image_url" class="pf-error">Debe subir una foto de perfil.</div>
            <div v-if="uploadErrors.image_url" class="pf-error">No se pudo subir la foto. Intente de nuevo.</div>
          </div>

          <!-- The name and the identification are given when creating the account, and shown on the credential: they
               only appear here for an application that still lacks them (from before the account asked for them). -->
          <div v-if="showNameFields || !idLocked" class="pf-grid">
            <template v-if="showNameFields">
              <PortalField
                v-for="f in identidadFields"
                :key="f.key"
                :def="f"
                :value="fieldValue(f)"
                :invalid="isInvalid(f)"
                :error="fieldError(f)"
                @input="(v) => setVal(f.key, v)"
                @blur="markTouched(f.key)"
              />
            </template>
            <!-- The identification gets a row of its own, under the name. -->
            <template v-if="!idLocked">
              <PortalField
                class="pf-row-start"
                :def="idTypeDef"
                :value="form.identification_type_id"
                @input="
                  (v) => {
                    setVal('identification_type_id', v);
                    resetGovId();
                  }
                "
              />
              <PortalField
                :def="govIdDef"
                :value="form.gov_id"
                :invalid="govIdInvalid"
                :error="govIdError"
                @input="
                  (v) => {
                    setVal('gov_id', v);
                    resetName();
                  }
                "
                @blur="handleGovIdEntry()"
                @enter="handleGovIdEntry()"
              >
                <template #append>
                  <button
                    v-if="isCedulaFisica"
                    type="button"
                    class="pf-btn pf-btn-input"
                    :disabled="!isValidGovId() || loadingPerson"
                    @click="handleGovIdEntry()"
                  >
                    {{ loadingPerson ? "Buscando…" : "Buscar" }}
                  </button>
                </template>
              </PortalField>
            </template>
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Datos demográficos</h3>
          <div class="pf-grid">
            <PortalField
              v-for="f in demograficosFields"
              :key="f.key"
              :def="f"
              :value="fieldValue(f)"
              :invalid="isInvalid(f)"
              :error="fieldError(f)"
              @input="(v) => setVal(f.key, v)"
              @blur="markTouched(f.key)"
            />
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Contacto</h3>
          <div class="pf-grid">
            <PortalField
              v-for="f in contactoFields"
              :key="f.key"
              :def="f"
              :value="fieldValue(f)"
              :invalid="isInvalid(f)"
              :error="fieldError(f)"
              @input="(v) => setVal(f.key, v)"
              @blur="markTouched(f.key)"
            />
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Familia y contacto de emergencia</h3>
          <div class="pf-grid">
            <PortalField
              v-for="f in [...familiaFields, ...emergFields]"
              :key="f.key"
              :def="f"
              :value="fieldValue(f)"
              :invalid="isInvalid(f)"
              :error="fieldError(f)"
              @input="(v) => setVal(f.key, v)"
              @blur="markTouched(f.key)"
            />
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Logística laboral</h3>
          <div class="pf-grid">
            <PortalField
              v-for="f in logisticaFields"
              :key="f.key"
              :def="f"
              :value="fieldValue(f)"
              :invalid="isInvalid(f)"
              :error="fieldError(f)"
              @input="(v) => setVal(f.key, v)"
              @blur="markTouched(f.key)"
            />
            <div class="pf-positions" :class="{ 'pf-invalid': positionsInvalid }">
              <label class="pf-label" for="pf-positions">Puestos a los que aplica</label>
              <multiselect
                id="pf-positions"
                v-model="form.positions"
                class="pf-multiselect"
                :class="{ invalid: positionsInvalid }"
                :multiple="true"
                :options="jobTypes"
                :loading="loadingJobTypes"
                :close-on-select="false"
                :clear-on-select="false"
                :preserve-search="true"
                label="name"
                track-by="id"
                placeholder="Seleccione uno o varios puestos"
                select-label=""
                selected-label=""
                deselect-label=""
                @close="markTouched('positions')"
              >
                <template #noResult>No hay puestos con ese nombre.</template>
                <template #noOptions>No se pudieron cargar los puestos.</template>
              </multiselect>
              <div v-if="positionsInvalid" class="pf-error">Seleccione al menos un puesto.</div>
            </div>
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Curriculum vitae</h3>
          <div
            v-drag-over
            class="pf-file"
            :class="{ 'pf-invalid': showStepError && !form.cv }"
            @dragover.prevent
            @drop.prevent="(e) => onFileDrop('cv', e)"
          >
            <span class="pf-file-icon"><PortalIcon name="file" :size="18" /></span>
            <div class="pf-file-body">
              <div class="pf-file-name">
                {{ uploadingKeys.cv ? "Subiendo…" : form.cv ? "Curriculum vitae adjunto" : "Sin adjuntar" }}
              </div>
              <div class="pf-file-hint">PDF o Word. También puede arrastrarlo aquí.</div>
            </div>
            <div class="pf-actions">
              <FileLink
                v-if="form.cv && !uploadingKeys.cv"
                title="Curriculum vitae"
                :files="[{ label: 'Curriculum vitae', prefix: form.cv }]"
                label="Ver archivo"
              />
              <label for="cv-file" class="pf-btn">
                <PortalIcon name="upload" :size="14" />
                <span>{{ form.cv ? "Reemplazar" : "Adjuntar" }}</span>
              </label>
              <button v-if="form.cv" type="button" class="pf-btn-link" @click="setVal('cv', null)">Quitar</button>
            </div>
            <input id="cv-file" class="pf-file-input" type="file" accept=".pdf,.doc,.docx" @change="(e) => onFileChange('cv', e)" />
          </div>
          <div v-if="showStepError && !form.cv" class="pf-error">Debe adjuntar su curriculum vitae.</div>
          <div v-if="uploadErrors.cv" class="pf-error">No se pudo subir el archivo. Intente de nuevo.</div>
        </section>
      </div>

      <!-- STEP 1: Documentación, edited in place in the portal: the section summary's document list (same icons,
           details and expiry badges, kept up to date while editing), each row with its controls on the right. -->
      <div v-if="step === 1" class="step-panel pd-step">
        <section class="pd-group">
          <h3 class="pf-group-title">Identificación y antecedentes</h3>
          <ul class="pd-list">
            <li class="pd-item" :class="{ 'pf-invalid': showStepError && cedulaMissing }">
              <div class="pd-row">
                <span class="pd-icon" :class="'tone-' + docRow('cedula').status.tone">
                  <PortalIcon name="id-card" :size="17" />
                </span>
                <div class="pd-main">
                  <div class="pd-name">Cédula / identificación</div>
                  <div class="pd-meta">
                    <StatusBadge :tone="docRow('cedula').status.tone" :label="docRow('cedula').status.label" />
                    <span>{{ docMeta(docRow("cedula")) }}</span>
                  </div>
                </div>
                <div class="pd-controls">
                  <!-- One button: the viewer switches between the front and the back. -->
                  <FileLink
                    v-if="docRow('cedula').files.length"
                    title="Cédula / identificación"
                    :files="docRow('cedula').files"
                    label="Ver"
                  />
                  <PortalUploadButton
                    v-for="side in cedulaSides"
                    :key="side.key"
                    class="pd-half"
                    compact
                    :label="side.label.toLowerCase()"
                    :filled="!!form[side.key]"
                    :uploading="!!uploadingKeys[side.key]"
                    :invalid="showStepError && !form[side.key]"
                    @upload="(file) => uploadSlimDoc(side.key, file)"
                    @remove="setVal(side.key, null)"
                  />
                  <label
                    class="pd-date"
                    :class="{ invalid: showStepError && !!docDateError(form.cedula_expiration_date, true), locked: cedulaExpirationLocked }"
                    :title="cedulaExpirationLocked ? 'Detectada del documento' : null"
                  >
                    <span>Vence el</span>
                    <input v-model="form.cedula_expiration_date" type="date" :disabled="cedulaExpirationLocked" />
                  </label>
                </div>
              </div>
              <ul v-if="cedulaErrors.length" class="pd-errors">
                <li v-for="error in cedulaErrors" :key="error">{{ error }}</li>
              </ul>
            </li>

            <li class="pd-item" :class="{ 'pf-invalid': showStepError && hojaMissing }">
              <div class="pd-row">
                <span class="pd-icon" :class="'tone-' + docRow('hoja_delincuencia').status.tone">
                  <PortalIcon name="id-card" :size="17" />
                </span>
                <div class="pd-main">
                  <div class="pd-name">Hoja de delincuencia</div>
                  <div class="pd-meta">
                    <StatusBadge
                      :tone="docRow('hoja_delincuencia').status.tone"
                      :label="docRow('hoja_delincuencia').status.label"
                    />
                    <span>{{ docMeta(docRow("hoja_delincuencia")) }}</span>
                  </div>
                </div>
                <div class="pd-controls">
                  <FileLink
                    v-if="form.hoja_delincuencia_url"
                    title="Hoja de delincuencia"
                    :files="docRow('hoja_delincuencia').files"
                    label="Ver"
                  />
                  <PortalUploadButton
                    label="documento"
                    :filled="!!form.hoja_delincuencia_url"
                    :uploading="!!uploadingKeys.hoja_delincuencia_url"
                    :invalid="showStepError && !form.hoja_delincuencia_url"
                    @upload="(file) => uploadSlimDoc('hoja_delincuencia_url', file)"
                    @remove="setVal('hoja_delincuencia_url', null)"
                  />
                  <label
                    class="pd-date"
                    :class="{
                      invalid: showStepError && !!docDateError(form.hoja_delincuencia_expiration_date, true),
                      locked: hojaDelincuenciaExpirationLocked,
                    }"
                    :title="hojaDelincuenciaExpirationLocked ? 'Detectada del documento' : null"
                  >
                    <span>Vence el</span>
                    <input
                      v-model="form.hoja_delincuencia_expiration_date"
                      type="date"
                      :disabled="hojaDelincuenciaExpirationLocked"
                    />
                  </label>
                </div>
              </div>
              <ul v-if="hojaErrors.length" class="pd-errors">
                <li v-for="error in hojaErrors" :key="error">{{ error }}</li>
              </ul>
            </li>
          </ul>
        </section>

        <section class="pd-group">
          <h3 class="pf-group-title">Licencias de conducir</h3>
          <ul v-if="licenseEntries.length" class="pd-list">
            <li
              v-for="(lic, idx) in licenseEntries"
              :key="'license-' + lic.uid"
              class="pd-item pd-removable"
              :class="{ 'pf-invalid': licenseErrors(lic).length && showStepError }"
            >
              <div class="pd-row">
                <span class="pd-icon" :class="'tone-' + docRow('license-' + idx).status.tone">
                  <PortalIcon name="id-card" :size="17" />
                </span>
                <div class="pd-main">
                  <div class="pd-name">{{ lic.license_id ? `Licencia de conducir ${lic.license_id}` : "Nueva licencia de conducir" }}</div>
                  <div class="pd-meta">
                    <StatusBadge :tone="docRow('license-' + idx).status.tone" :label="docRow('license-' + idx).status.label" />
                    <span>{{ docMeta(docRow("license-" + idx)) }}</span>
                  </div>
                </div>
                <div class="pd-controls">
                  <!-- Not a native <select>: its list is drawn by the browser, tiny in Chrome's phone emulation. -->
                  <div class="pd-date pd-select" :class="{ invalid: showStepError && licenseTypeMissing(lic) }">
                    <span>Tipo</span>
                    <multiselect
                      :value="licenseOption(lic.license_id)"
                      class="pd-multiselect"
                      :options="groupedLicenses"
                      group-values="types"
                      group-label="label"
                      :group-select="false"
                      label="license_id"
                      track-by="license_id"
                      placeholder="Seleccione…"
                      :searchable="false"
                      :allow-empty="false"
                      :show-labels="false"
                      @input="(option) => (lic.license_id = option.license_id)"
                    />
                  </div>
                  <FileLink
                    v-if="lic.file_url"
                    :title="`Licencia de conducir ${lic.license_id}`"
                    :files="docRow('license-' + idx).files"
                    label="Ver"
                  />
                  <PortalUploadButton
                    label="documento"
                    :filled="!!lic.file_url"
                    :uploading="lic.uploading"
                    :invalid="showStepError && licenseFileMissing(lic)"
                    @upload="(file) => uploadLicenseFile(lic, file)"
                    @remove="clearLicenseFile(lic)"
                  />
                  <label
                    v-if="lic.file_url"
                    class="pd-date"
                    :class="{ invalid: showStepError && !!docDateError(lic.expiration_date, false), locked: lic.expirationLocked }"
                    :title="lic.expirationLocked ? 'Detectada del documento' : null"
                  >
                    <span>Vence el</span>
                    <input v-model="lic.expiration_date" type="date" :disabled="lic.expirationLocked" />
                  </label>
                  <button type="button" class="pd-remove" title="Quitar licencia" aria-label="Quitar licencia" @click="removeLicenseEntry(idx)">
                    <PortalIcon name="x" :size="15" />
                  </button>
                </div>
              </div>
              <ul v-if="(showStepError && licenseErrors(lic).length) || lic.error" class="pd-errors">
                <li v-if="lic.error">No se pudo subir el archivo. Intente de nuevo.</li>
                <template v-if="showStepError">
                  <li v-for="error in licenseErrors(lic)" :key="error">{{ error }}</li>
                </template>
              </ul>
            </li>
          </ul>
          <p v-else class="pd-empty">No registró licencias de conducir.</p>
          <button type="button" class="pd-add" @click="addLicenseEntry()">
            <span aria-hidden="true">+</span> Agregar licencia
          </button>
        </section>

        <section class="pd-group">
          <h3 class="pf-group-title">Requisitos de agente de seguridad</h3>
          <ul class="pd-list">
            <li
              v-for="doc in securityDocs"
              :key="doc.key"
              class="pd-item"
              :class="{ 'pf-invalid': showStepError && securityErrors(doc).length }"
            >
              <div class="pd-row">
                <span class="pd-icon" :class="'tone-' + docRow(doc.key).status.tone">
                  <PortalIcon name="shield" :size="17" />
                </span>
                <div class="pd-main">
                  <div class="pd-name">{{ doc.label }}</div>
                  <div class="pd-meta">
                    <StatusBadge :tone="docRow(doc.key).status.tone" :label="docRow(doc.key).status.label" />
                    <span v-if="docMeta(docRow(doc.key))">{{ docMeta(docRow(doc.key)) }}</span>
                  </div>
                </div>
                <div class="pd-controls">
                  <div
                    class="pd-segmented"
                    :class="{ invalid: showStepError && docSiNo(doc) === '' }"
                    role="radiogroup"
                    :aria-label="`¿Cuenta con ${doc.label}?`"
                    title="¿Cuenta con este requisito?"
                  >
                    <button
                      v-for="answer in ['Sí', 'No']"
                      :key="answer"
                      type="button"
                      role="radio"
                      class="pd-segment"
                      :class="{ selected: docSiNo(doc) === answer }"
                      :aria-checked="docSiNo(doc) === answer ? 'true' : 'false'"
                      @click="setDocSiNo(doc, answer)"
                    >
                      {{ answer }}
                    </button>
                  </div>
                  <template v-if="docSiNo(doc) === 'Sí'">
                    <FileLink v-if="docRow(doc.key).files.length" :title="doc.label" :files="docRow(doc.key).files" label="Ver" />
                    <PortalUploadButton
                      label="documento"
                      :filled="!!form[doc.photoKey]"
                      :uploading="!!uploadingKeys[doc.photoKey]"
                      :invalid="showStepError && !form[doc.photoKey]"
                      @upload="(file) => uploadDocPhoto(doc, file)"
                      @remove="setVal(doc.photoKey, null)"
                    />
                    <label class="pd-date" :class="{ invalid: showStepError && !!docDateError(form[doc.dateKey], true) }">
                      <span>{{ doc.isExpiry ? "Vence el" : "Realizado el" }}</span>
                      <input :value="form[doc.dateKey]" type="date" @input="setVal(doc.dateKey, $event.target.value)" />
                    </label>
                  </template>
                </div>
              </div>
              <ul v-if="(showStepError && securityErrors(doc).length) || uploadErrors[doc.photoKey]" class="pd-errors">
                <li v-if="uploadErrors[doc.photoKey]">No se pudo subir el archivo. Intente de nuevo.</li>
                <template v-if="showStepError">
                  <li v-for="error in securityErrors(doc)" :key="error">{{ error }}</li>
                </template>
              </ul>
            </li>
          </ul>
        </section>

        <section class="pd-group">
          <h3 class="pf-group-title">Referencias</h3>
          <ul v-if="referenceEntries.length" class="pd-list">
            <li v-for="(reference, idx) in referenceEntries" :key="'reference-' + reference.uid" class="pd-item pd-removable">
              <div class="pd-row">
                <span class="pd-icon" :class="reference.file_url ? 'tone-success' : 'tone-neutral'">
                  <PortalIcon name="file" :size="17" />
                </span>
                <div class="pd-main">
                  <div class="pd-name">Referencia {{ idx + 1 }}</div>
                  <div class="pd-meta">
                    <StatusBadge
                      :tone="reference.file_url ? 'success' : 'neutral'"
                      :label="reference.file_url ? 'Cargado' : 'Sin adjuntar'"
                    />
                    <span>{{ reference.file_url ? "Documento adjunto." : "Sin adjuntar." }}</span>
                  </div>
                </div>
                <div class="pd-controls">
                  <FileLink
                    v-if="reference.file_url"
                    :title="`Referencia ${idx + 1}`"
                    :files="[{ label: 'Documento', prefix: reference.file_url }]"
                    label="Ver"
                  />
                  <PortalUploadButton
                    label="documento"
                    :filled="!!reference.file_url"
                    :uploading="reference.uploading"
                    :removable="false"
                    @upload="(file) => uploadReferenceFile(reference, file)"
                  />
                  <button
                    type="button"
                    class="pd-remove"
                    title="Quitar referencia"
                    aria-label="Quitar referencia"
                    @click="removeReferenceEntry(idx)"
                  >
                    <PortalIcon name="x" :size="15" />
                  </button>
                </div>
              </div>
              <ul v-if="reference.error" class="pd-errors">
                <li>No se pudo subir el archivo. Intente de nuevo.</li>
              </ul>
            </li>
          </ul>
          <p v-else class="pd-empty">No registró referencias.</p>
          <button type="button" class="pd-add" @click="addReferenceEntry()">
            <span aria-hidden="true">+</span> Agregar referencia
          </button>
        </section>
      </div>

      <!-- STEP 2: Residencia, edited in place in the portal: the section summary's look (the address, the map, the
           croquis), each part with its fields. -->
      <div v-if="step === 2" class="step-panel pf-step pr-step">
        <section class="pf-group">
          <h3 class="pf-group-title">Dirección</h3>
          <div class="pf-grid pr-grid">
            <PortalField
              :def="provinceDef"
              :value="form.province_id"
              :invalid="isInvalid(provinceDef)"
              :error="fieldError(provinceDef)"
              @input="
                (v) => {
                  setVal('province_id', v);
                  loadCantons();
                }
              "
              @blur="markTouched('province_id')"
            />
            <PortalField
              :def="cantonDef"
              :value="form.canton_id"
              :invalid="isInvalid(cantonDef)"
              :error="fieldError(cantonDef)"
              @input="
                (v) => {
                  setVal('canton_id', v);
                  loadDistricts();
                }
              "
              @blur="markTouched('canton_id')"
            />
            <PortalField
              :def="districtDef"
              :value="form.district_id"
              :invalid="isInvalid(districtDef)"
              :error="fieldError(districtDef)"
              @input="
                (v) => {
                  setVal('district_id', v);
                  recenterToDistrict();
                }
              "
              @blur="markTouched('district_id')"
            />
            <PortalField
              :def="addressDef"
              :value="form.address_detail"
              :invalid="isInvalid(addressDef)"
              :error="fieldError(addressDef)"
              @input="(v) => setVal('address_detail', v)"
              @blur="markTouched('address_detail')"
            />
          </div>
        </section>

        <section class="pf-group">
          <div class="pr-head">
            <h3 class="pf-group-title">Ubicación en el mapa</h3>
            <div v-if="hasPin" class="pr-links">
              <button type="button" class="pf-btn-link" @click="clearMap()">Quitar pin</button>
              <a :href="mapsUrl" target="_blank" rel="noopener" class="pr-maps-link">
                <span>Abrir en Google Maps</span>
                <PortalIcon name="external" :size="13" />
              </a>
            </div>
          </div>
          <p class="pr-hint">Haga clic en el mapa para marcar su vivienda, o busque la dirección. Puede arrastrar el pin.</p>
          <div v-if="locateError" class="pf-error pr-error">{{ locateError }}</div>
          <div class="pr-map" :class="{ 'pf-invalid': showStepError && !hasPin }">
            <GmapAutocomplete
              v-if="loadedLocation"
              class="pr-search"
              placeholder="Buscar dirección…"
              :options="{ componentRestrictions: { country: 'cr' } }"
              @place_changed="onPlaceSelected"
            />
            <GmapMap
              v-if="loadedLocation"
              ref="mapRef"
              :center="mapCenter"
              :zoom="mapZoom"
              :options="{ streetViewControl: false, fullscreenControl: true, gestureHandling: 'cooperative' }"
              class="pr-gmap"
              @click="mark"
            >
              <GmapMarker v-if="hasPin" :position="marker" :clickable="!readOnlyMode" :draggable="!readOnlyMode" @dragend="mark" />
            </GmapMap>
          </div>
          <div v-if="showStepError && !hasPin" class="pf-error pr-error">Debe marcar su ubicación en el mapa.</div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Croquis de residencia</h3>
          <div
            v-drag-over
            class="pf-file"
            :class="{ 'pf-invalid': !!uploadErrors.croquis_url }"
            @dragover.prevent
            @drop.prevent="(e) => onFileDrop('croquis_url', e)"
          >
            <span class="pf-file-icon"><PortalIcon name="map-pin" :size="18" /></span>
            <div class="pf-file-body">
              <div class="pf-file-name">
                {{ uploadingKeys.croquis_url ? "Subiendo…" : form.croquis_url ? "Croquis adjunto" : "No adjuntó croquis (opcional)" }}
              </div>
              <div class="pf-file-hint">{{ uploads[0].hint }}</div>
            </div>
            <div class="pf-actions">
              <FileLink
                v-if="form.croquis_url && !uploadingKeys.croquis_url"
                title="Croquis de residencia"
                :files="[{ label: 'Croquis', prefix: form.croquis_url }]"
                label="Ver archivo"
              />
              <label for="croquis-file" class="pf-btn">
                <PortalIcon name="upload" :size="14" />
                <span>{{ form.croquis_url ? "Reemplazar" : "Adjuntar" }}</span>
              </label>
              <button v-if="form.croquis_url" type="button" class="pf-btn-link" @click="setVal('croquis_url', null)">
                Quitar
              </button>
            </div>
            <input
              id="croquis-file"
              class="pf-file-input"
              type="file"
              accept="image/*,.pdf"
              @change="(e) => onFileChange('croquis_url', e)"
            />
          </div>
          <div v-if="uploadErrors.croquis_url" class="pf-error">No se pudo subir el archivo. Intente de nuevo.</div>
        </section>
      </div>

      <!-- STEP 3: Declaración jurada, signed in the portal's DeclarationModal: the declaration, its acceptance and the
           signature pad. -->
      <div v-if="step === 3" class="step-panel pf-step pg-step">
        <section class="pf-group">
          <h3 class="pf-group-title">Declaración jurada</h3>
          <div class="pg-text">
            <p>
              Yo, <strong>{{ declName }}</strong
              >, número de identificación <strong>{{ form.gov_id || "________" }}</strong
              >, declaro bajo juramento que toda la información suministrada en esta solicitud es verdadera,
              completa y exacta. Reconozco que cualquier omisión, falsedad o alteración de la información
              proporcionada podrá ser considerada como falta grave y constituirá causa suficiente para la no
              contratación, o de comprobarse posteriormente, para la terminación de la relación laboral sin
              responsabilidad patronal.
            </p>
            <p>
              Asimismo, autorizo expresamente a Corporación K-9 para verificar mis antecedentes judiciales,
              laborales y personales, así como para el tratamiento de mis datos personales conforme a la legislación
              vigente.
            </p>
            <p>
              Corporación K-9 garantiza la confidencialidad y seguridad de esta información, conforme a la Ley N.º
              8968 de Protección de la Persona frente al tratamiento de sus Datos Personales. Autorizo que mi
              información sea utilizada para procesos de selección actuales y futuros dentro de la empresa.
            </p>
          </div>
          <label class="pg-accept" :class="{ 'pf-invalid': showStepError && !form.sworn_statement_accepted }">
            <input v-model="form.sworn_statement_accepted" type="checkbox" />
            <span>Acepto la declaración jurada y el tratamiento de mis datos personales.</span>
          </label>
          <div v-if="showStepError && !form.sworn_statement_accepted" class="pf-error">
            Debe aceptar para enviar la solicitud.
          </div>
          <div class="pf-grid pg-date">
            <PortalField :def="declDateDef" :value="declDateText" />
          </div>
        </section>

        <section class="pf-group">
          <h3 class="pf-group-title">Firma</h3>
          <div class="pg-sign" :class="{ 'pf-invalid': showStepError && !form.signature_url && !hasSignatureDrawing }">
            <SignaturePad
              ref="signaturePad"
              :saved-url="signaturePreviewUrl"
              :invalid="showStepError && !form.signature_url && !hasSignatureDrawing"
              @drawing-change="hasSignatureDrawing = $event"
              @clear="setVal('signature_url', null)"
            />
          </div>
        </section>
      </div>
      </fieldset>
    </div>

    <div v-if="!done && !readOnlyMode && !isSectionEditor" class="wizard-footer">
      <div class="wizard-footer-inner">
        <button
          type="button"
          class="btn-outline"
          :style="{ visibility: step === 0 ? 'hidden' : 'visible' }"
          @click="back()"
        >
          &larr; Atrás
        </button>
        <div class="step-count">Paso {{ step + 1 }} de 4</div>
        <button type="button" class="btn-gold" :disabled="submitting" @click="next()">
          {{ step === 3 ? (submitting ? "Enviando…" : "Enviar solicitud") : "Guardar y continuar →" }}
        </button>
      </div>
    </div>

    <div
      v-if="existingApplicationModal"
      class="modal-overlay"
      @click.self="existingApplicationModal = false"
    >
      <div class="modal-card">
        <button
          type="button"
          class="modal-close"
          aria-label="Cerrar"
          @click="existingApplicationModal = false"
        >
          &times;
        </button>
        <div class="modal-icon">!</div>
        <div class="modal-title">Ya tiene una solicitud activa</div>
        <p class="modal-text">
          Encontramos que esta identificación ya completó y envió una
          solicitud anteriormente. No es posible volver a cargarla ni
          modificarla desde aquí.
        </p>
        <p class="modal-text">
          Si necesita corregir algo o tiene dudas sobre su proceso, por
          favor contacte al equipo de Recursos Humanos de K-9 Internacional.
        </p>
        <button type="button" class="btn-gold" @click="existingApplicationModal = false">
          Entendido
        </button>
      </div>
    </div>

    <div v-if="noticeModal.visible" class="modal-overlay" @click.self="closeNotice()">
      <div class="modal-card" :class="'modal-card-' + noticeModal.variant">
        <button type="button" class="modal-close" aria-label="Cerrar" @click="closeNotice()">
          &times;
        </button>
        <div class="modal-icon">{{ noticeModal.variant === "info" ? "i" : "!" }}</div>
        <div class="modal-title">{{ noticeModal.title }}</div>
        <p class="modal-text">{{ noticeModal.message }}</p>
        <ul v-if="noticeModal.items.length" class="modal-list">
          <li v-for="(item, idx) in noticeModal.items" :key="idx">{{ item }}</li>
        </ul>
        <button type="button" class="btn-gold" @click="closeNotice()">Entendido</button>
      </div>
    </div>

    <ConfirmDialog
      v-if="locationPrompt"
      variant="info"
      title="¿Utilizar su ubicación actual?"
      message="Podemos marcar en el mapa el lugar donde se encuentra ahora como su residencia."
      confirm-label="Sí, usar mi ubicación"
      cancel-label="No, la marco yo"
      @confirm="acceptLocationPrompt()"
      @cancel="locationPrompt = false"
    />
  </div>
</template>

<script>
import {
  BLOOD_TYPES,
  COUNTRIES,
  GENDERS,
  ID_TYPES,
  ALL_LICENSE_TYPES_BY_GROUPS,
  SECURITY_DOCS,
} from "../constants";
import * as candidateApi from "../services/candidateApi";
import { geocodeAddress } from "../services/geocodeApi";
import SignaturePad from "./wizard/SignaturePad.vue";
import SiteHeader from "./SiteHeader.vue";
import ConfirmDialog from "./ConfirmDialog.vue";
import FileLink from "./portal/FileLink.vue";
import PortalField from "./portal/PortalField.vue";
import PortalIcon from "./portal/PortalIcon.vue";
import PortalUploadButton from "./portal/PortalUploadButton.vue";
import StatusBadge from "./portal/StatusBadge.vue";
import { documentMeta, documentRows, formatDate } from "./portal/candidateSummary";

// Today as YYYY-MM-DD in the applicant's own time zone (toISOString() would give the UTC day,
// which in Costa Rica is already "tomorrow" from 6 p. m.).
function localISODate(date = new Date()) {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const UPLOAD_ERROR = "No se pudo subir el archivo. Intente de nuevo.";

function newReferenceEntry(uid, fileUrl = null) {
  return { uid, file_url: fileUrl, uploading: false, error: false };
}

// Autosave of a section the applicant already completed (portal, see the `autosave` prop): the section's name in
// PATCH /candidate/me/record/<section>, and the payload keys it saves, as buildPayload() sends them.
const AUTOSAVE_SECTIONS = { 0: "personal", 1: "documents", 2: "residence" };
const AUTOSAVE_KEYS = {
  0: [
    "first_name",
    "last_name",
    "sur_name",
    "birthdate",
    "gender_id",
    "country_id",
    "blood_type_id",
    "social_security_number",
    "whatsapp_phone",
    "cell_phone",
    "phone",
    "email",
    "father_name",
    "mother_name",
    "emergency_contact_name",
    "emergency_contact_phone",
    "own_transport",
    "willing_to_move",
    "positions",
    "image_url",
    "cv",
  ],
  1: [
    "cedula_front_url",
    "cedula_back_url",
    "cedula_expiration_date",
    "hoja_delincuencia_url",
    "hoja_delincuencia_expiration_date",
    ...SECURITY_DOCS.reduce((keys, doc) => keys.concat(doc.key, doc.dateKey, doc.photoKey), []),
    "driving_licenses",
    "reference_documents",
  ],
  2: ["province_id", "canton_id", "district_id", "address_detail", "latitude", "longitude", "croquis_url"],
};
// What is typed waits for a pause before it is saved; the email, which changes the account, waits longer. The rest
// (choices, files, the map pin) goes right away.
const AUTOSAVE_TYPING_DELAY = 1000;
const AUTOSAVE_EMAIL_DELAY = 10000;
const AUTOSAVE_CHOICE_DELAY = 150;
const AUTOSAVE_TYPED_KEYS = new Set([
  "first_name",
  "last_name",
  "sur_name",
  "birthdate",
  "social_security_number",
  "whatsapp_phone",
  "cell_phone",
  "phone",
  "father_name",
  "mother_name",
  "emergency_contact_name",
  "emergency_contact_phone",
  "cedula_expiration_date",
  "hoja_delincuencia_expiration_date",
  ...SECURITY_DOCS.map((doc) => doc.dateKey),
  "driving_licenses",
  "address_detail",
]);
// How a saved change is named in the portal's notice, for the keys that aren't one of the step's fields.
const AUTOSAVE_LABELS = {
  positions: "Puestos a los que aplica",
  image_url: "Foto de perfil",
  cv: "Curriculum vitae",
  cedula_front_url: "Cédula / identificación",
  cedula_back_url: "Cédula / identificación",
  cedula_expiration_date: "Cédula / identificación",
  hoja_delincuencia_url: "Hoja de delincuencia",
  hoja_delincuencia_expiration_date: "Hoja de delincuencia",
  driving_licenses: "Licencias de conducir",
  reference_documents: "Referencias",
  latitude: "Ubicación en el mapa",
  longitude: "Ubicación en el mapa",
  croquis_url: "Croquis de residencia",
};
// A save that failed for the connection (or the backend asked to slow down) is tried again after this long.
const AUTOSAVE_RETRY_DELAY = 10000;

export default {
  name: "NewApplicationForm",
  components: {
    SignaturePad,
    SiteHeader,
    ConfirmDialog,
    FileLink,
    PortalField,
    PortalIcon,
    PortalUploadButton,
    StatusBadge,
  },
  props: {
    // The logged-in account's email: the email field starts from it (see applyDraft()), and a different one saved
    // is reported through "email-changed" (see saveSection()).
    userEmail: { type: String, default: "" },
    // Portal section editor: shows only this step (0-3), without the
    // header/stepper/footer, prefilled from initialData (the /candidate/me
    // response) instead of loading it itself. The portal saves it through
    // saveSection().
    section: { type: Number, default: null },
    initialData: { type: Object, default: null },
    // The section is edited in place in the portal (see InlineSectionEditor) instead of in its editor modal: a
    // missing field is pointed out on the field itself, and changes are reported through "dirty"/"missing"/"busy".
    inline: { type: Boolean, default: false },
    // Inline, a section the applicant already completed: each change is saved by itself (see runAutosave()) instead
    // of with saveSection(), and reported through "autosaved" / "autosave-blocked" / "autosave-failed".
    autosave: { type: Boolean, default: false },
  },
  data() {
    const todayISO = localISODate();
    return {
      step: this.section !== null ? this.section : 0,
      showStepError: false,
      submitting: false,
      lastDraftGovId: null,
      draftCheckPromise: null,
      // The gov_id last copied into social_security_number automatically (see autofillSocialSecurity()).
      autoFilledSocialSecurity: null,
      // gov_id of the application loaded from the logged-in account, if any.
      accountGovId: null,
      // Section editor state right after loading, to tell whether closing it discards changes.
      initialStateKey: "",
      existingApplicationModal: false,
      // Asking whether to pin the applicant's current location (see offerUseCurrentLocation()).
      locationPrompt: false,
      completedApplicationGovId: null,
      // True while reviewing an already-submitted application read-only.
      readOnlyMode: false,
      // Single modal reused for every info/warning/error notice.
      noticeModal: { visible: false, variant: "info", title: "", message: "", items: [] },
      done: false,
      touched: {},

      idTypes: ID_TYPES,
      countryCodes: COUNTRIES,
      bloodTypes: BLOOD_TYPES,
      groupedLicenses: ALL_LICENSE_TYPES_BY_GROUPS,
      jobTypes: [],
      loadingJobTypes: false,
      loadingPerson: false,
      // Tracks the cédula física lookup: idle | found | not_found.
      personLookupState: "idle",

      provinces: [],
      cantons: [],
      districts: [],
      loadingProvinces: false,
      loadingCantons: false,
      loadingDistricts: false,

      driving_licenses: [],
      licenseEntries: [
        {
          uid: 1,
          license_id: "",
          file_url: null,
          uploading: false,
          error: false,
          expiration_date: "",
          expirationLocked: false,
        },
      ],
      licenseUidSeq: 2,

      referenceEntries: [newReferenceEntry(1)],
      referenceUidSeq: 2,

      mapCenter: { lat: 9.9333, lng: -84.0833 },
      mapZoom: 13,
      marker: { lat: 9.9333, lng: -84.0833 },
      hasPin: false,
      loadedLocation: true,
      geocodeRequestId: 0,
      locatingMe: false,
      locateError: "",

      // Fields with a file uploading right now ({ cv: true, … }), and how many uploads are in flight.
      uploadingKeys: {},
      uploadsInFlight: 0,
      // The email the backend refused when saving ({ email: "x@y.com" lowercased, message }), shown on its field for
      // as long as the field still holds it.
      emailRejection: null,
      // Whether SignaturePad currently has an unsaved drawing on its canvas.
      hasSignatureDrawing: false,
      // Autosave: the section's values (by payload key, as JSON) as last saved, and as they were the last time any of
      // them changed; its timers (the email has its own, longer one) and the save on its way, if any.
      autosaveSaved: null,
      autosaveSeen: null,
      autosaveTimer: null,
      autosaveEmailTimer: null,
      autosaveRetryTimer: null,
      autosaveEmailDue: false,
      autosaveInFlight: null,
      autosaveAgain: false,
      // The changes the backend refused (as JSON), not sent again as they are; the missing fields last reported; and
      // whether the failure notice was already given for the current run of failed saves.
      autosaveRejected: "",
      autosaveBlocked: "",
      autosaveFailing: false,
      uploadErrors: {},
      // Locks cedula_expiration_date once auto-extracted from the upload.
      cedulaExpirationLocked: false,
      // Same as cedulaExpirationLocked, for hoja de delincuencia.
      hojaDelincuenciaExpirationLocked: false,
      // Temporary signed URLs for previewing the avatar/signature images.
      avatarPreviewUrl: null,
      signaturePreviewUrl: null,

      securityDocs: SECURITY_DOCS,

      // Only croquis stays in this generic upload grid now.
      uploads: [
        {
          key: "croquis_url",
          label: "Croquis de residencia",
          required: false,
          kind: "file",
          hint:
            "Representación gráfica de la ubicación exacta de su vivienda, que indique las vías de acceso, los puntos de referencia cercanos y las señas necesarias para llegar al domicilio.",
        },
      ],

      form: {
        // ---- existing fields (SimpleForm.vue) — keys/semantics unchanged ----
        identification_type_id: 1,
        gov_id: "",
        first_name: "",
        last_name: "",
        sur_name: "",
        phone: "",
        birthdate: "",
        cell_phone: "",
        email: this.userEmail || "",
        image_url: null,
        cv: null,
        gender_id: "1",
        blood_type_id: null,
        country_id: "CR",
        province_id: null,
        canton_id: null,
        district_id: null,
        address_detail: "",
        positions: [],
        willing_to_move: "",
        up_to_date_portation: "",
        own_transport: "",

        // ---- new fields for this refactor ----
        social_security_number: "",
        whatsapp_phone: "",
        father_name: "",
        mother_name: "",
        emergency_contact_name: "",
        emergency_contact_phone: "",
        latitude: null,
        longitude: null,
        cedula_front_url: null,
        cedula_back_url: null,
        cedula_expiration_date: "",
        hoja_delincuencia_url: null,
        hoja_delincuencia_expiration_date: "",
        croquis_url: null,
        portation_expiration_date: "",
        portation_photo: null,
        basic_course: "",
        basic_course_date: "",
        basic_course_photo: null,
        fingerprints: "",
        fingerprints_expiration_date: "",
        fingerprints_photo: null,
        psych_exam: "",
        psych_exam_expiration_date: "",
        psych_exam_photo: null,
        weapons_course: "",
        weapons_course_date: "",
        weapons_course_photo: null,
        avsec_course: "",
        avsec_course_date: "",
        avsec_course_photo: null,
        sworn_statement_accepted: false,
        sworn_statement_date: todayISO,
        signature_url: null,
      },
    };
  },

  computed: {
    isSectionEditor() {
      return this.section !== null;
    },
    referencesPayload() {
      return this.referenceEntries.filter((entry) => entry.file_url).map((entry) => ({ file_url: entry.file_url }));
    },
    stepMeta() {
      return ["Datos personales", "Documentación", "Residencia", "Declaración"];
    },
    pct() {
      return Math.round(((this.step + (this.done ? 1 : 0)) / 4) * 100);
    },
    isCedulaFisica() {
      return this.form.identification_type_id == 1;
    },
    // The account's identification is what the applicant logs in with (chosen before creating it), so it can't change here.
    idLocked() {
      return !!this.accountGovId;
    },
    nameFieldsVisible() {
      if (!this.isCedulaFisica) return true;
      // Also visible if a loaded draft already has a name.
      return this.personLookupState !== "idle" || !!this.form.first_name;
    },
    nameFieldsLocked() {
      if (!this.isCedulaFisica) return false;
      return this.personLookupState === "found";
    },
    // The name comes with the account (from the padrón, or typed in "Cree su cuenta"): Datos personales only asks for
    // it when the saved application lacks it and the padrón doesn't have it either.
    showNameFields() {
      const saved = this.initialData || {};
      if (saved.first_name && saved.last_name && saved.sur_name) return false;
      return !this.nameFieldsLocked;
    },
    idTypeDef() {
      return this.def("identification_type_id", "Tipo de identificación", "select", true, this.idTypes, "", "", false, "off", this.idLocked);
    },
    govIdDef() {
      return this.def("gov_id", "Número de identificación", "text", true, null, "", "", false, "off", this.idLocked);
    },
    // A cédula física has to be looked up (it brings the name) before the section can be saved.
    govIdError() {
      if (!this.form.gov_id) return "Campo obligatorio";
      if (this.isCedulaFisica && this.personLookupState === "idle" && !this.form.first_name) return "Presione «Buscar» para validar su cédula";
      return "";
    },
    govIdInvalid() {
      return !this.idLocked && (!!this.touched.gov_id || this.showStepError) && this.govIdError !== "";
    },
    positionsInvalid() {
      return (!!this.touched.positions || this.showStepError) && (!this.form.positions || this.form.positions.length === 0);
    },
    busy() {
      return this.uploadsInFlight > 0;
    },
    // Documentación's rows as the section summary builds them (status, badge, files), from what's on screen.
    liveDocuments() {
      return documentRows({ ...this.form, driving_licenses: this.licenseEntries, reference_documents: [] });
    },
    // The cédula's two files, each in its own field.
    cedulaSides() {
      return [
        { key: "cedula_front_url", label: "Frente" },
        { key: "cedula_back_url", label: "Reverso" },
      ];
    },
    cedulaMissing() {
      return !this.form.cedula_front_url || !this.form.cedula_back_url || !!this.docDateError(this.form.cedula_expiration_date, true);
    },
    cedulaErrors() {
      const errors = [];
      if (this.uploadErrors.cedula_front_url || this.uploadErrors.cedula_back_url) errors.push(UPLOAD_ERROR);
      if (!this.showStepError) return errors;
      if (!this.form.cedula_front_url) errors.push("Adjunte el frente de su cédula.");
      if (!this.form.cedula_back_url) errors.push("Adjunte el reverso de su cédula.");
      const dateError = this.docDateError(this.form.cedula_expiration_date, true);
      if (dateError) errors.push(dateError);
      return errors;
    },
    hojaMissing() {
      return !this.form.hoja_delincuencia_url || !!this.docDateError(this.form.hoja_delincuencia_expiration_date, true);
    },
    hojaErrors() {
      const errors = [];
      if (this.uploadErrors.hoja_delincuencia_url) errors.push(UPLOAD_ERROR);
      if (!this.showStepError) return errors;
      if (!this.form.hoja_delincuencia_url) errors.push("Adjunte la hoja de delincuencia.");
      const dateError = this.docDateError(this.form.hoja_delincuencia_expiration_date, true);
      if (dateError) errors.push(dateError);
      return errors;
    },
    // Residencia's fields: the places in lists of our own (PortalSelect), each one waiting for the one before it.
    provinceDef() {
      const options = this.provinces.map((place) => ({ value: place.id, text: place.name }));
      return { ...this.def("province_id", "Provincia", "select", true, options), custom: true };
    },
    cantonDef() {
      const options = this.cantons.map((place) => ({ value: place.id, text: place.name }));
      return { ...this.def("canton_id", "Cantón", "select", true, options), custom: true, inactive: options.length === 0 };
    },
    districtDef() {
      const options = this.districts.map((place) => ({ value: place.id, text: place.name }));
      return { ...this.def("district_id", "Distrito", "select", true, options), custom: true, inactive: options.length === 0 };
    },
    addressDef() {
      return this.def("address_detail", "Detalles de la dirección", "textarea", true, null, "Barrio, señas exactas, referencias…", "", true);
    },
    mapsUrl() {
      return `https://www.google.com/maps/search/?api=1&query=${this.marker.lat},${this.marker.lng}`;
    },
    // The date of the declaration is the day it is signed: shown, not edited.
    declDateDef() {
      return this.def("sworn_statement_date", "Fecha", "text", true, null, "", "", false, "off", true);
    },
    declDateText() {
      return formatDate(this.todayISO());
    },
    // What the portal's credential shows of the open "Datos personales" while it is being edited: the photo, the blood
    // type and the phone, as they stand in the form (see the "live" event and CandidatePortal).
    liveProfile() {
      return {
        image_url: this.form.image_url,
        avatar_url: this.avatarPreviewUrl,
        blood_type_id: this.form.blood_type_id,
        whatsapp_phone: this.form.whatsapp_phone,
        cell_phone: this.form.cell_phone,
      };
    },
    // Whether anything differs from what was loaded (see editableStateKey()). With autosave, whether something isn't
    // saved yet (waiting for its pause, on its way, or not savable as it is).
    dirty() {
      if (this.autosave) {
        if (!this.autosaveSaved) return false;
        const values = this.autosaveValues;
        return !!this.autosaveInFlight || Object.keys(values).some((key) => values[key] !== this.autosaveSaved[key]);
      }
      return !!this.initialStateKey && this.editableStateKey() !== this.initialStateKey;
    },
    // Autosave: the section's values by payload key, each as JSON (what is compared with what was saved).
    autosaveValues() {
      if (!this.autosave || !AUTOSAVE_KEYS[this.step]) return null;
      const payload = this.payloadValues();
      const values = {};
      AUTOSAVE_KEYS[this.step].forEach((key) => {
        values[key] = JSON.stringify(payload[key] === undefined ? null : payload[key]);
      });
      return values;
    },
    // Required fields still missing, counted once a save has asked for them (showStepError).
    missingCount() {
      return this.showStepError ? this.stepMissingFields(this.step).length : 0;
    },
    declName() {
      const nm = [this.form.first_name, this.form.last_name, this.form.sur_name]
        .filter(Boolean)
        .join(" ");
      return nm || "____________________";
    },
    identidadFields() {
      if (!this.nameFieldsVisible) return [];
      return [
        this.def("first_name", "Nombre", "text", true, null, "", "", false, "new-password", this.nameFieldsLocked),
        this.def("last_name", "Primer apellido", "text", true, null, "", "", false, "new-password", this.nameFieldsLocked),
        this.def("sur_name", "Segundo apellido", "text", true, null, "", "", false, "new-password", this.nameFieldsLocked),
      ];
    },
    demograficosFields() {
      return [
        // max = today 18 years ago: the calendar opens there and blocks any more-recent (under-18) date.
        { ...this.def("birthdate", "Fecha de nacimiento", "date", true), max: this.maxBirthdate() },
        this.def("gender_id", "Género", "select", true, GENDERS),
        this.def("nacionalidad_country_id", "Nacionalidad", "select", true, this.countryOptions()),
        this.def("blood_type_id", "Tipo de sangre", "select", true, this.bloodOptions()),
        {
          ...this.def("social_security_number", "Número de seguro social", "text", true),
          hint: "(Mismo que identificación para cédula física)",
        },
      ];
    },
    contactoFields() {
      return [
        this.def("whatsapp_phone", "Celular (WhatsApp)", "tel", true, null, "8888-8888", "####-####"),
        this.def("cell_phone", "Celular (llamadas)", "tel", true, null, "8888-8888", "####-####"),
        this.def("phone", "Teléfono de habitación", "tel", false, null, "8888-8888", "####-####"),
        // The account's email, which can be changed: the backend refuses one that someone else already has (see
        // emailRejection). Smaller text, so an address fits in the field.
        { ...this.def("email", "Correo electrónico", "email", true, null, "", "", false, "email"), small: true },
      ];
    },
    familiaFields() {
      return [
        this.def("father_name", "Nombre del padre", "text", true, null, "", "", false, "new-password"),
        this.def("mother_name", "Nombre de la madre", "text", true, null, "", "", false, "new-password"),
      ];
    },
    logisticaFields() {
      return [
        this.def("own_transport", "Transporte propio", "radio", true, this.siNoOptions()),
        // oneLine: wider than its column (the one beside it is empty), so it reaches over into the next one.
        { ...this.def("willing_to_move", "Disponible para trabajar en otra región", "radio", true, this.siNoOptions()), oneLine: true },
      ];
    },
    dpFields() {
      return [
        ...this.identidadFields,
        ...this.demograficosFields,
        ...this.contactoFields,
        ...this.familiaFields,
        ...this.emergFields,
        ...this.logisticaFields,
      ];
    },
    emergFields() {
      return [
        // oneLine: its label (196px) is a hair wider than a column at some screen widths, but never wraps.
        { ...this.def("emergency_contact_name", "Nombre de contacto de emergencia", "text", true, null, "", "", false, "new-password"), oneLine: true },
        this.def("emergency_contact_phone", "Teléfono de emergencia", "tel", true, null, "8888-8888", "####-####"),
      ];
    },
  },

  mounted() {
    this.loadProvinces();
    this.loadRolePositions();
    if (this.isSectionEditor) {
      if (this.initialData) this.applyAccountDraft(this.initialData);
      if (this.inline) {
        // A list edited in place shows only what's there: no blank license or reference waiting to be filled in.
        this.licenseEntries = this.licenseEntries.filter((entry) => entry.license_id || entry.file_url);
        this.referenceEntries = this.referenceEntries.filter((entry) => entry.file_url);
      }
      this.prefillFromAccountId();
      this.initialStateKey = this.editableStateKey();
      if (this.autosaveValues) {
        // What is on screen once loaded is what is saved: from here on, each change is saved by itself.
        this.autosaveSaved = { ...this.autosaveValues };
        this.autosaveSeen = { ...this.autosaveValues };
        window.addEventListener("beforeunload", this.onAutosaveUnload);
        document.addEventListener("visibilitychange", this.onAutosaveVisibility);
      }
      // Starting over (or opening) puts the credential back on what is saved.
      if (this.inline && this.step === 0) this.$emit("live", this.liveProfile);
      return;
    }
    this.loadMyDraft().finally(() => this.maybeAutoLocate());
  },

  beforeDestroy() {
    clearTimeout(this.autosaveTimer);
    clearTimeout(this.autosaveEmailTimer);
    clearTimeout(this.autosaveRetryTimer);
    window.removeEventListener("beforeunload", this.onAutosaveUnload);
    document.removeEventListener("visibilitychange", this.onAutosaveVisibility);
  },

  watch: {
    autosaveValues(values) {
      if (values) this.onAutosaveValues(values);
    },
    // A save held back while a file uploaded goes once it is done.
    busy(value) {
      this.$emit("busy", value);
      if (!value && this.autosaveSaved && this.dirty) this.runAutosave();
    },
    liveProfile(value) {
      if (this.inline && this.step === 0) this.$emit("live", value);
    },
    dirty(value) {
      this.$emit("dirty", value);
    },
    missingCount(value) {
      this.$emit("missing", value);
    },
    "form.image_url"(val) {
      if (!val) this.avatarPreviewUrl = null;
    },
    "form.signature_url"(val) {
      if (!val) this.signaturePreviewUrl = null;
    },
  },

  methods: {
    todayISO() {
      return localISODate();
    },
    // A complete YYYY-MM-DD date with a plausible year. Date inputs let the year be typed past
    // 4 digits (202555, or 2555 in Chrome, which keeps the last 4), so the raw value isn't enough.
    isValidDate(value) {
      const match = /^(\d{4})-\d{2}-\d{2}$/.exec(value || "");
      if (!match || isNaN(new Date(value + "T00:00:00"))) return false;
      const year = Number(match[1]);
      return year >= 1900 && year <= 2100;
    },
    maxBirthdate() {
      const d = new Date();
      d.setFullYear(d.getFullYear() - 18);
      return d.toISOString().slice(0, 10);
    },
    def(key, label, type, required, options, placeholder, mask, wide, autocomplete, disabled) {
      return {
        key,
        label,
        type,
        required,
        options: options || [],
        placeholder: placeholder || "",
        mask: mask || "",
        wide: !!wide,
        // "new-password" resists Chrome's autofill better than "off" for name fields.
        autocomplete: autocomplete || "off",
        disabled: !!disabled,
      };
    },
    countryOptions() {
      return this.countryCodes.map((c) => ({ value: c.value, text: c.text }));
    },
    bloodOptions() {
      return this.bloodTypes.map((b) => ({ value: b.value, text: b.text }));
    },
    siNoOptions() {
      return [
        { value: "true", text: "Sí" },
        { value: "false", text: "No" },
      ];
    },

    fieldKeyFor(f) {
      return f.key === "nacionalidad_country_id" ? "country_id" : f.key;
    },
    fieldValue(f) {
      return this.form[this.fieldKeyFor(f)];
    },
    setVal(key, value) {
      if (this.readOnlyMode) return;
      const realKey = key === "nacionalidad_country_id" ? "country_id" : key;
      this.$set(this.form, realKey, value);
      this.$set(this.touched, realKey, true);
      if (value === null) this.$set(this.uploadErrors, realKey, false);
      if ((realKey === "cedula_front_url" || realKey === "cedula_back_url") && value === null) {
        this.cedulaExpirationLocked = false;
        this.form.cedula_expiration_date = "";
      }
      if (realKey === "hoja_delincuencia_url" && value === null) {
        this.hojaDelincuenciaExpirationLocked = false;
        this.form.hoja_delincuencia_expiration_date = "";
      }
    },
    markTouched(key) {
      this.$set(this.touched, key, true);
    },

    getError(f) {
      const key = this.fieldKeyFor(f);
      const val = this.form[key];
      if (f.required && (val === "" || val === null || val === undefined)) {
        return "Campo obligatorio";
      }
      if (val === "" || val === null || val === undefined) return "";
      if (f.type === "date" && !this.isValidDate(val)) return "Fecha no válida";
      if (f.type === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(val).trim())) {
        return "Correo no válido";
      }
      if (key === "email" && this.emailRejection && this.emailRejection.email === String(val).trim().toLowerCase()) {
        return this.emailRejection.message;
      }
      if (f.type === "tel" && !/^\d{4}-?\d{4}$/.test(val)) {
        return "Deben ser 8 dígitos";
      }
      if (key === "birthdate") {
        const age = (Date.now() - new Date(val).getTime()) / (365.25 * 864e5);
        if (age < 18) return "Debe ser mayor de edad";
        if (age > 75) return "Fecha no válida";
      }
      return "";
    },
    isInvalid(f) {
      const key = this.fieldKeyFor(f);
      const touched = !!this.touched[key] || this.showStepError;
      return touched && this.getError(f) !== "";
    },
    fieldError(f) {
      return this.getError(f);
    },

    resetGovId() {
      // A number copied in automatically came from the old identification type, so it goes with it.
      if (this.autoFilledSocialSecurity && this.form.social_security_number === this.autoFilledSocialSecurity) {
        this.form.social_security_number = "";
      }
      this.autoFilledSocialSecurity = null;
      this.form.gov_id = "";
      this.resetName();
    },
    resetName() {
      this.form.first_name = "";
      this.form.last_name = "";
      this.form.sur_name = "";
      this.personLookupState = "idle";
    },
    // Cédula física: the social security number is the identification number, so it's filled in once
    // that's entered. A different number the applicant typed (or a saved one) is left alone.
    autofillSocialSecurity() {
      if (!this.isCedulaFisica || !this.isValidGovId()) return;
      const current = this.form.social_security_number;
      if (current && current !== this.autoFilledSocialSecurity) return;
      this.setVal("social_security_number", this.form.gov_id);
      this.autoFilledSocialSecurity = this.form.gov_id;
    },
    isValidGovId() {
      const govId = this.form.gov_id;
      const amount = govId ? String(govId).length : 0;
      return amount > 8;
    },
    // With the identification locked (see idLocked), what entering it would trigger runs on opening instead:
    // the social security number copied from a cédula física, and the registry lookup that fills in the name.
    prefillFromAccountId() {
      if (!this.idLocked || !this.isCedulaFisica) return;
      this.autofillSocialSecurity();
      if (!this.form.first_name) this.loadPerson({ rebaseline: true });
    },
    // rebaseline: the lookup ran on its own (see prefillFromAccountId()), so the name it fills in isn't an unsaved change.
    loadPerson({ rebaseline = false } = {}) {
      if (!this.isValidGovId() || this.loadingPerson) return;
      this.loadingPerson = true;
      candidateApi
        .getPerson(this.form.gov_id)
        .then((response) => {
          const pristine = rebaseline && !this.hasUnsavedChanges();
          const name = response.data;
          this.form.first_name = (name && name.first_name) || "";
          this.form.last_name = (name && name.last_name) || "";
          this.form.sur_name = (name && name.sur_name) || "";
          this.personLookupState = "found";
          if (pristine) this.initialStateKey = this.editableStateKey();
        })
        .catch(() => {
          this.personLookupState = "not_found";
        })
        .finally(() => {
          this.loadingPerson = false;
        });
    },

    // Which application_step values are safe to reload/prefill — keep in sync with CandidateFormStateView.RESUMABLE_STEPS in rrhh-backend.
    isResumableApplicationStep(applicationStep) {
      return applicationStep === 1 || applicationStep === 2 || applicationStep === 3;
    },

    // Fired when the applicant finishes entering gov_id — checks for a saved application first, then runs the cédula física lookup.
    handleGovIdEntry() {
      if (!this.isValidGovId()) return;
      const govId = this.form.gov_id;
      if (this.completedApplicationGovId === govId) {
        this.existingApplicationModal = true;
        return;
      }
      this.checkSavedProgress().then((blocked) => {
        if (blocked) return;
        // After the saved application (if any) is loaded, so it can't blank the number again.
        this.autofillSocialSecurity();
        if (this.isCedulaFisica) this.loadPerson();
      });
    },

    // Looks up a saved application by gov_id; blocks if not resumable, otherwise reloads it via applyDraft().
    checkSavedProgress() {
      if (!this.isValidGovId()) return Promise.resolve(false);
      const govId = this.form.gov_id;
      // The account's own cédula: its application is already loaded, and looking it up again
      // would reload it over unsaved edits (or, once submitted, report it as "already active").
      if (govId === this.accountGovId) return Promise.resolve(false);
      // Reuses the in-flight promise so blur + "Buscar" don't double-fire.
      if (this.lastDraftGovId === govId) return this.draftCheckPromise;
      this.lastDraftGovId = govId;
      this.draftCheckPromise = candidateApi
        .getCandidateDraft(govId)
        .then((response) => {
          const data = response.data;
          if (!data) return false;
          // This gov_id belongs to a different account — never load its data here.
          if (data.taken_by_other_account) {
            this.showNotice({
              variant: "warning",
              title: "Número de identificación ya registrado",
              message:
                "Ya existe una solicitud asociada a este número de identificación con otra cuenta. Verifique el número ingresado; si cree que se trata de un error, contacte al equipo de Recursos Humanos de K-9 Internacional.",
            });
            return true;
          }
          if (!this.isResumableApplicationStep(data.application_step)) {
            this.completedApplicationGovId = govId;
            this.existingApplicationModal = true;
            return true;
          }
          this.applyDraft(data);
          return false;
        })
        .catch(() => false); // 404 — no saved application yet for this gov_id
      return this.draftCheckPromise;
    },

    // Loads the logged-in account's saved candidate on mount, if any.
    loadMyDraft() {
      return candidateApi
        .getMyCandidateDraft()
        .then((response) => {
          const data = response.data;
          if (!data || data.application_step === 0 || data.application_step == null) return;
          if (data.application_step === 4) {
            // Already submitted — reload it editable so the applicant can update and re-save.
            this.applyAccountDraft(data);
            this.showNotice({
              variant: "info",
              title: "Su solicitud ya fue enviada",
              message:
                "Puede revisar y editar la información que envió. Al guardar sus cambios, su solicitud quedará actualizada.",
            });
            return;
          }
          if (!this.isResumableApplicationStep(data.application_step)) {
            this.existingApplicationModal = true;
            return;
          }
          this.applyAccountDraft(data);
        })
        .catch(() => {}); // 404 — no candidate linked to this account yet
    },

    // Silent: logging in already implies "here's your application".
    applyAccountDraft(data) {
      this.accountGovId = data.gov_id || null;
      this.applyDraft(data, { silent: true });
    },

    applyDraft(data, { silent = false } = {}) {
      const skipKeys = new Set(["driving_licenses", "positions"]);
      // For cédula física, a resolved registry match wins over the draft's name.
      if (this.isCedulaFisica && this.personLookupState === "found") {
        skipKeys.add("first_name");
        skipKeys.add("last_name");
        skipKeys.add("sur_name");
      }
      Object.keys(data).forEach((key) => {
        if (skipKeys.has(key)) return;
        if (!(key in this.form)) return;
        const value = data[key];
        // Never signed yet ("" from the backend): keep today's date — the field is disabled, so a blank one could never be filled in.
        if (key === "sworn_statement_date" && !value) return;
        if (value !== null && value !== undefined) {
          this.$set(this.form, key, value);
        }
      });
      // Email always stays the logged-in account's, never the draft's.
      this.form.email = this.userEmail || this.form.email;

      if (Array.isArray(data.positions) && data.positions.length) {
        this.form.positions = data.positions;
      }

      if (Array.isArray(data.driving_licenses) && data.driving_licenses.length) {
        this.licenseEntries = data.driving_licenses.map((entry, idx) => ({
          uid: idx + 1,
          license_id: entry.license_id || "",
          file_url: entry.file_url || null,
          uploading: false,
          error: false,
          expiration_date: entry.expiration_date || "",
          expirationLocked: false,
        }));
        this.licenseUidSeq = this.licenseEntries.length + 1;
      }

      if (Array.isArray(data.reference_documents) && data.reference_documents.length) {
        this.referenceEntries = data.reference_documents.map((entry, idx) => newReferenceEntry(idx + 1, entry.file_url));
        this.referenceUidSeq = this.referenceEntries.length + 1;
      }

      if (data.latitude != null && data.longitude != null) {
        this.marker = { lat: data.latitude, lng: data.longitude };
        this.mapCenter = { lat: data.latitude, lng: data.longitude };
        this.hasPin = true;
      }

      this.loadLocationOptionsFor(data.province_id, data.canton_id);
      // A past session's signed preview link is long expired — fetch a fresh one.
      this.refreshAvatarPreview(this.form.image_url);
      this.refreshSignaturePreview(this.form.signature_url);
      if (!silent) {
        this.showNotice({
          variant: "info",
          title: "Encontramos su solicitud guardada",
          message:
            "Cargamos los datos que ya había ingresado con esta identificación. Revise cada paso para verificar la información antes de continuar.",
        });
      }
    },

    // Populates canton/district options for a loaded draft without resetting its province/canton/district ids.
    loadLocationOptionsFor(provinceId, cantonId) {
      if (!provinceId) return;
      candidateApi
        .getCantons(provinceId)
        .then((response) => {
          const results = response.data;
          if (results) this.cantons = results.results || results;
        })
        .catch(() => {});
      if (!cantonId) return;
      candidateApi
        .getDistricts(provinceId, cantonId)
        .then((response) => {
          const results = response.data;
          if (results) this.districts = results.results || results;
        })
        .catch(() => {});
    },

    loadProvinces() {
      this.loadingProvinces = true;
      candidateApi
        .getProvinces()
        .then((response) => {
          const results = response.data;
          if (results) this.provinces = results.results || results;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingProvinces = false;
        });
    },
    loadCantons() {
      this.cantons = [];
      this.districts = [];
      this.form.canton_id = null;
      this.form.district_id = null;
      const provinceId = this.form.province_id;
      if (!provinceId) return;
      this.recenterToProvince();
      this.loadingCantons = true;
      candidateApi
        .getCantons(provinceId)
        .then((response) => {
          const results = response.data;
          if (results) this.cantons = results.results || results;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingCantons = false;
        });
    },
    loadDistricts() {
      this.districts = [];
      this.form.district_id = null;
      const provinceId = this.form.province_id;
      const cantonId = this.form.canton_id;
      if (!provinceId || !cantonId) return;
      this.recenterToCanton();
      this.loadingDistricts = true;
      candidateApi
        .getDistricts(provinceId, cantonId)
        .then((response) => {
          const results = response.data;
          if (results) this.districts = results.results || results;
        })
        .catch(() => {})
        .finally(() => {
          this.loadingDistricts = false;
        });
    },

    // Re-centers/zooms the map viewport only — never touches the pin/marker.
    geocodeAndCenter(query, zoom) {
      const requestId = ++this.geocodeRequestId;
      geocodeAddress(query)
        .then((response) => {
          if (requestId !== this.geocodeRequestId) return;
          const data = response.data;
          if (!data || data.error) return;
          const lat = parseFloat(data.latt);
          const lng = parseFloat(data.longt);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            this.mapCenter = { lat, lng };
            this.mapZoom = zoom;
          }
        })
        .catch(() => {});
    },
    recenterToProvince() {
      const province = this.provinces.find((p) => p.id === this.form.province_id);
      if (!province) return;
      this.geocodeAndCenter(`${province.name}, Costa Rica`, 10);
    },
    recenterToCanton() {
      const province = this.provinces.find((p) => p.id === this.form.province_id);
      const canton = this.cantons.find((c) => c.id === this.form.canton_id);
      if (!canton) return;
      const parts = [canton.name, province ? province.name : "", "Costa Rica"].filter(Boolean);
      this.geocodeAndCenter(parts.join(", "), 12);
    },
    recenterToDistrict() {
      const province = this.provinces.find((p) => p.id === this.form.province_id);
      const canton = this.cantons.find((c) => c.id === this.form.canton_id);
      const district = this.districts.find((d) => d.id === this.form.district_id);
      if (!district) return;
      const parts = [
        district.name,
        canton ? canton.name : "",
        province ? province.name : "",
        "Costa Rica",
      ].filter(Boolean);
      this.geocodeAndCenter(parts.join(", "), 14);
      this.offerUseCurrentLocation();
    },
    // Once province/canton/district are all set, offers to auto-pin the applicant's current location.
    offerUseCurrentLocation() {
      if (this.hasPin) return;
      this.locationPrompt = true;
    },
    acceptLocationPrompt() {
      this.locationPrompt = false;
      this.useLocation();
    },
    loadRolePositions() {
      this.loadingJobTypes = true;
      candidateApi
        .getRolePositions()
        .then((response) => {
          const results = response.data;
          if (results) this.jobTypes = results.results || results;
        })
        .catch(() => {
          this.jobTypes = [];
        })
        .finally(() => {
          this.loadingJobTypes = false;
        });
    },

    transformLicenses() {
      this.driving_licenses = this.licensesPayload();
    },
    licensesPayload() {
      // Same output shape SimpleForm.vue always sent: {number, license_id}.
      return this.licenseEntries
        .filter((entry) => entry.license_id)
        .map((entry) => {
          const chars = entry.license_id.split("");
          return {
            number: +chars[1],
            license_id: chars[0],
            file_url: entry.file_url || null,
            expiration_date: entry.expiration_date || null,
          };
        });
    },
    addLicenseEntry() {
      this.licenseEntries.push({
        uid: this.licenseUidSeq++,
        license_id: "",
        file_url: null,
        uploading: false,
        error: false,
        expiration_date: "",
        expirationLocked: false,
      });
    },
    removeLicenseEntry(idx) {
      this.licenseEntries.splice(idx, 1);
    },
    // Licenses are optional, but a started one needs both its type and its document.
    // The catalog's entry for a license type ("B1"), or null while none is chosen.
    licenseOption(licenseId) {
      return [].concat(...this.groupedLicenses.map((group) => group.types)).find((type) => type.license_id === licenseId) || null;
    },
    licenseTypeMissing(lic) {
      return !!lic.file_url && !lic.license_id;
    },
    licenseFileMissing(lic) {
      return !!lic.license_id && !lic.file_url;
    },
    uploadLicenseFile(entry, file) {
      entry.uploading = true;
      entry.error = false;
      this.trackUpload(
        "license-" + entry.uid,
        candidateApi
          .uploadFile(file)
          .then((response) => {
            if (!response.data) throw new Error("upload response missing url");
            entry.file_url = response.data;
          })
          .catch(() => {
            entry.error = true;
          })
          .finally(() => {
            entry.uploading = false;
          })
      );
      this.extractLicenseExpiration(entry, file);
    },
    clearLicenseFile(entry) {
      entry.file_url = null;
      entry.error = false;
      entry.expirationLocked = false;
      entry.expiration_date = "";
    },
    addReferenceEntry() {
      this.referenceEntries.push(newReferenceEntry(this.referenceUidSeq++));
    },
    removeReferenceEntry(idx) {
      this.referenceEntries.splice(idx, 1);
    },
    uploadReferenceFile(entry, file) {
      entry.uploading = true;
      entry.error = false;
      this.trackUpload(
        "reference-" + entry.uid,
        candidateApi
          .uploadFile(file)
          .then((response) => {
            if (!response.data) throw new Error("upload response missing url");
            entry.file_url = response.data;
          })
          .catch(() => {
            entry.error = true;
          })
          .finally(() => {
            entry.uploading = false;
          })
      );
    },
    // Same best-effort pattern as extractCedulaExpiration, for a license entry.
    extractLicenseExpiration(entry, file) {
      entry.expirationLocked = false;
      candidateApi
        .extractDocumentExpiration(file)
        .then((response) => {
          const date = response.data && response.data.expiration_date;
          if (date) {
            entry.expiration_date = date;
            entry.expirationLocked = true;
          }
        })
        .catch(() => {});
    },

    mark(event) {
      if (this.readOnlyMode) return;
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      this.marker = { lat, lng };
      this.hasPin = true;
      this.form.latitude = lat;
      this.form.longitude = lng;
    },
    clearMap() {
      this.hasPin = false;
      this.form.latitude = null;
      this.form.longitude = null;
    },
    useLocation() {
      this.locateError = "";
      if (!navigator.geolocation) {
        this.locateError = "Este navegador no soporta geolocalización.";
        return;
      }
      // Geolocation silently fails over plain http:// (non-secure context).
      if (!window.isSecureContext) {
        this.locateError = "Requiere una conexión segura (https) para funcionar.";
        return;
      }
      this.locatingMe = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.marker = { lat, lng };
          this.mapCenter = { lat, lng };
          this.mapZoom = 17;
          this.hasPin = true;
          this.form.latitude = lat;
          this.form.longitude = lng;
          this.locatingMe = false;
        },
        (err) => {
          this.locatingMe = false;
          this.locateError =
            err.code === err.PERMISSION_DENIED
              ? "Permiso de ubicación denegado."
              : "No se pudo obtener su ubicación. Intente de nuevo.";
        }
      );
    },
    // On mobile, silently pre-fills the pin if geolocation was already granted.
    maybeAutoLocate() {
      if (this.readOnlyMode || this.hasPin || !navigator.geolocation) return;
      if (!window.matchMedia || !window.matchMedia("(max-width: 525px)").matches) return;
      if (!navigator.permissions || !navigator.permissions.query) return;
      navigator.permissions
        .query({ name: "geolocation" })
        .then((status) => {
          if (status.state === "granted" && !this.hasPin) this.useLocation();
        })
        .catch(() => {});
    },
    onPlaceSelected(place) {
      if (this.readOnlyMode || !place || !place.geometry || !place.geometry.location) return;
      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();
      this.marker = { lat, lng };
      this.mapCenter = { lat, lng };
      this.mapZoom = 17;
      this.hasPin = true;
      this.form.latitude = lat;
      this.form.longitude = lng;
    },

    docSiNo(doc) {
      if (doc.key === "up_to_date_portation") {
        if (this.form.up_to_date_portation === "true") return "Sí";
        if (this.form.up_to_date_portation === "false") return "No";
        return "";
      }
      return this.form[doc.key] || "";
    },
    setDocSiNo(doc, siNo) {
      if (doc.key === "up_to_date_portation") {
        this.form.up_to_date_portation = siNo === "Sí" ? "true" : "false";
      } else {
        this.form[doc.key] = siNo;
      }
      this.touched[doc.key] = true;
    },
    expiryInfo(dateStr) {
      if (!dateStr) return { show: false };
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const d = new Date(dateStr + "T00:00:00");
      if (isNaN(d)) return { show: false };
      const days = Math.round((d - today) / 86400000);
      if (days < 0) {
        const ago = this.dateDiffYMD(d, today);
        return {
          show: true,
          text: `Documento vencido hace ${this.formatDuration(ago.years, ago.months, ago.days)}`,
          color: "#9a2820",
          bg: "#fbecea",
          bc: "#e6bdb7",
        };
      }
      if (days === 0) {
        return { show: true, text: "Vence hoy", color: "#9a2820", bg: "#fbecea", bc: "#e6bdb7" };
      }
      if (days <= 30) {
        return { show: true, text: `Vence en ${days} día(s)`, color: "#8a5a00", bg: "#fbf3e4", bc: "#e8cf9a" };
      }
      const { years, months, days: dayPart } = this.dateDiffYMD(today, d);
      return {
        show: true,
        text: `Vigente · vence en ${this.formatDuration(years, months, dayPart)}`,
        color: "#215c3d",
        bg: "#e9f3ec",
        bc: "#bcdcc7",
      };
    },
    // Calendar-accurate (not averaged) breakdown of the gap between two dates, `to` on/after `from`.
    dateDiffYMD(from, to) {
      let years = to.getFullYear() - from.getFullYear();
      let months = to.getMonth() - from.getMonth();
      let days = to.getDate() - from.getDate();
      if (days < 0) {
        months -= 1;
        days += new Date(to.getFullYear(), to.getMonth(), 0).getDate(); // days in the month before `to`
      }
      if (months < 0) {
        years -= 1;
        months += 12;
      }
      return { years, months, days };
    },
    // "3 años 4 meses y 5 días" — zero-valued units are dropped, e.g. "4 meses y 5 días".
    formatDuration(years, months, days) {
      const parts = [];
      if (years > 0) parts.push(`${years} ${years === 1 ? "año" : "años"}`);
      if (months > 0) parts.push(`${months} ${months === 1 ? "mes" : "meses"}`);
      if (days > 0 || parts.length === 0) parts.push(`${days} ${days === 1 ? "día" : "días"}`);
      if (parts.length === 1) return parts[0];
      return `${parts.slice(0, -1).join(" ")} y ${parts[parts.length - 1]}`;
    },

    // Marks `key` as uploading until `promise` settles (see busy).
    trackUpload(key, promise) {
      this.uploadsInFlight += 1;
      this.$set(this.uploadingKeys, key, true);
      return promise.finally(() => {
        this.uploadsInFlight -= 1;
        this.$set(this.uploadingKeys, key, false);
      });
    },
    uploadDoc(u, file) {
      this.$set(this.uploadErrors, u.key, false);
      return this.trackUpload(
        u.key,
        candidateApi
          .uploadFile(file)
          .then((response) => {
            const url = response.data;
            if (!url) throw new Error("upload response missing url");
            this.form[u.key] = url;
          })
          .catch(() => {
            this.$set(this.uploadErrors, u.key, true);
          })
      );
    },
    docChipLabel(url) {
      return /\.pdf$/i.test(url || "") ? "PDF" : "IMG";
    },
    // The cédula's sides and the hoja de delincuencia: uploaded, and their expiration date read from them.
    uploadSlimDoc(key, file) {
      this.uploadDoc({ key }, file);
      if (key === "cedula_front_url" || key === "cedula_back_url") {
        this.extractCedulaExpiration(file);
      }
      if (key === "hoja_delincuencia_url") this.extractHojaDelincuenciaExpiration(file);
    },
    docRow(key) {
      return this.liveDocuments.find((row) => row.key === key);
    },
    // The details beside the badge; an unanswered requirement's would only repeat it ("Sin responder").
    docMeta(row) {
      return row.answer === "" ? "" : documentMeta(row);
    },
    // "" when the date is fine; an empty optional one is fine too.
    docDateError(value, required) {
      if (!value) return required ? "Indique la fecha." : "";
      return this.isValidDate(value) ? "" : "La fecha no es válida.";
    },
    licenseErrors(lic) {
      const errors = [];
      if (this.licenseTypeMissing(lic)) errors.push("Seleccione el tipo de licencia.");
      if (this.licenseFileMissing(lic)) errors.push("Adjunte el documento de la licencia.");
      const dateError = lic.file_url ? this.docDateError(lic.expiration_date, false) : "";
      if (dateError) errors.push(dateError);
      return errors;
    },
    securityErrors(doc) {
      const answer = this.docSiNo(doc);
      if (answer === "") return ["Indique si cuenta con este requisito."];
      if (answer !== "Sí") return [];
      const errors = [];
      if (!this.form[doc.photoKey]) errors.push("Adjunte el documento.");
      const dateError = this.docDateError(this.form[doc.dateKey], true);
      if (dateError) errors.push(dateError);
      return errors;
    },
    // Tries either cédula side (front or back — either might carry the date); a miss leaves an already-found date from the other side untouched.
    extractCedulaExpiration(file) {
      candidateApi
        .extractDocumentExpiration(file)
        .then((response) => {
          const date = response.data && response.data.expiration_date;
          if (date) {
            this.form.cedula_expiration_date = date;
            this.cedulaExpirationLocked = true;
          }
        })
        .catch(() => {});
    },
    // Same as extractCedulaExpiration, but through the hoja-de-delincuencia-specific OCR endpoint.
    extractHojaDelincuenciaExpiration(file) {
      this.hojaDelincuenciaExpirationLocked = false;
      candidateApi
        .extractCriminalRecordExpiration(file)
        .then((response) => {
          const date = response.data && response.data.expiration_date;
          if (date) {
            this.form.hoja_delincuencia_expiration_date = date;
            this.hojaDelincuenciaExpirationLocked = true;
          }
        })
        .catch(() => {});
    },
    // A plain file field (the cv, the croquis): the picked or dropped file is uploaded to that field.
    onFileChange(key, e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.uploadDoc({ key, kind: "file" }, file);
      e.target.value = "";
    },
    onFileDrop(key, e) {
      const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.uploadDoc({ key, kind: "file" }, file);
    },
    onAvatarChange(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.uploadAvatar(file);
      e.target.value = "";
    },
    uploadDocPhoto(doc, file) {
      this.$set(this.uploadErrors, doc.photoKey, false);
      this.trackUpload(
        doc.photoKey,
        candidateApi
          .uploadFile(file)
          .then((response) => {
            if (!response.data) throw new Error("upload response missing url");
            this.form[doc.photoKey] = response.data;
          })
          .catch(() => {
            this.$set(this.uploadErrors, doc.photoKey, true);
          })
      );
    },

    // Exchanges the stored Spaces key for a temporary signed URL to preview.
    refreshAvatarPreview(prefix) {
      if (!prefix) {
        this.avatarPreviewUrl = null;
        return Promise.resolve();
      }
      return candidateApi
        .getFileUrl(prefix)
        .then((response) => {
          this.avatarPreviewUrl = response.data || null;
        })
        .catch(() => {
          this.avatarPreviewUrl = null;
        });
    },
    refreshSignaturePreview(prefix) {
      if (!prefix) {
        this.signaturePreviewUrl = null;
        return Promise.resolve();
      }
      return candidateApi
        .getFileUrl(prefix)
        .then((response) => {
          this.signaturePreviewUrl = response.data || null;
        })
        .catch(() => {
          this.signaturePreviewUrl = null;
        });
    },

    uploadAvatar(file) {
      this.$set(this.uploadErrors, "image_url", false);
      this.trackUpload(
        "image_url",
        candidateApi
          .uploadFile(file)
          .then((response) => {
            const prefix = response.data;
            if (!prefix) throw new Error("upload response missing url");
            this.form.image_url = prefix;
            return this.refreshAvatarPreview(prefix);
          })
          .catch(() => {
            this.$set(this.uploadErrors, "image_url", true);
          })
      );
    },

    // Uploads a freshly-captured signature blob; called from submit() before saving.
    saveSignatureBlob(blob) {
      const file = new File([blob], "signature.png", { type: "image/png" });
      return candidateApi.uploadFile(file).then((response) => {
        const prefix = response.data;
        if (!prefix) throw new Error("upload response missing url");
        this.form.signature_url = prefix;
        return this.refreshSignaturePreview(prefix);
      });
    },

    stepFields(step) {
      if (step === 0) return this.dpFields;
      if (step === 2) {
        return [
          this.def("province_id", "Provincia", "select", true),
          this.def("canton_id", "Cantón", "select", true),
          this.def("district_id", "Distrito", "select", true),
          this.def("address_detail", "Detalles de la dirección", "text", true),
        ];
      }
      if (step === 3) return [this.def("sworn_statement_date", "Fecha de la declaración", "date", true)];
      return [];
    },
    // Labels of every required field still missing/invalid for a step.
    stepMissingFields(step) {
      const missing = [];
      for (const f of this.stepFields(step)) {
        if (this.getError(f) !== "") missing.push(f.label);
      }
      if (step === 0) {
        // Required by POST /candidate for every save — without it the save fails.
        if (!this.form.gov_id) missing.push("Número de identificación");
        if (!this.form.positions || this.form.positions.length === 0) {
          missing.push("Puestos a los que aplica");
        }
        if (this.isCedulaFisica && this.personLookupState === "idle" && !this.form.first_name) {
          missing.push('Búsqueda de cédula (presione "Buscar")');
        }
        if (!this.form.image_url) missing.push("Foto de perfil");
        if (!this.form.cv) missing.push("Curriculum vitae");
      }
      if (step === 1) {
        if (!this.form.cedula_front_url) missing.push("Cédula / identificación (frente)");
        if (!this.form.cedula_back_url) missing.push("Cédula / identificación (reverso)");
        if (!this.form.cedula_expiration_date) {
          missing.push("Cédula / identificación (fecha de vencimiento)");
        } else if (!this.isValidDate(this.form.cedula_expiration_date)) {
          missing.push("Cédula / identificación (fecha de vencimiento no válida)");
        }
        if (!this.form.hoja_delincuencia_url) missing.push("Hoja de delincuencia (documento)");
        if (!this.form.hoja_delincuencia_expiration_date) {
          missing.push("Hoja de delincuencia (fecha de vencimiento)");
        } else if (!this.isValidDate(this.form.hoja_delincuencia_expiration_date)) {
          missing.push("Hoja de delincuencia (fecha de vencimiento no válida)");
        }
        this.licenseEntries.forEach((lic, idx) => {
          if (this.licenseTypeMissing(lic)) missing.push(`Licencia ${idx + 1} (tipo de licencia)`);
          if (this.licenseFileMissing(lic)) missing.push(`Licencia ${idx + 1} (documento)`);
          if (lic.expiration_date && !this.isValidDate(lic.expiration_date)) {
            missing.push(`Licencia ${idx + 1} (fecha de vencimiento no válida)`);
          }
        });
        for (const doc of this.securityDocs) {
          const val = this.docSiNo(doc);
          if (val === "") {
            missing.push(doc.label);
            continue;
          }
          if (val === "Sí") {
            if (!this.form[doc.dateKey]) missing.push(`${doc.label} (${doc.dateLabel})`);
            else if (!this.isValidDate(this.form[doc.dateKey])) missing.push(`${doc.label} (${doc.dateLabel} no válida)`);
            if (!this.form[doc.photoKey]) missing.push(`${doc.label} (documento)`);
          }
        }
      }
      if (step === 2) {
        if (!this.hasPin) missing.push("Ubicación en el mapa");
        for (const u of this.uploads) {
          if (u.required && !this.form[u.key]) missing.push(u.label);
        }
      }
      if (step === 3) {
        if (!this.form.sworn_statement_accepted) missing.push("Aceptar la declaración jurada");
        if (!this.form.signature_url && !this.hasSignatureDrawing) missing.push("Firma");
      }
      return missing;
    },
    stepValid(step) {
      return this.stepMissingFields(step).length === 0;
    },
    markStepTouched(step) {
      this.stepFields(step).forEach((f) => {
        this.$set(this.touched, this.fieldKeyFor(f), true);
      });
      this.showStepError = true;
    },

    showNotice({ variant, title, message, items }) {
      this.noticeModal = { visible: true, variant, title, message, items: items || [] };
    },
    closeNotice() {
      this.noticeModal.visible = false;
    },
    showMissingFieldsNotice(step) {
      this.markStepTouched(step);
      if (this.inline) {
        this.focusFirstInvalid();
        return;
      }
      this.showNotice({
        variant: "warning",
        title: "Faltan campos por completar",
        message: "Complete los siguientes campos obligatorios para continuar:",
        items: this.stepMissingFields(step),
      });
    },

    // Declaración, already signed: shows the declaration and a blank signature pad to sign it again.
    // Inline: brings the first field marked as missing into view, instead of listing them in a notice.
    focusFirstInvalid() {
      this.$nextTick(() => {
        const field = this.$el.querySelector(".pf-invalid");
        if (!field) return;
        field.scrollIntoView({ behavior: "smooth", block: "center" });
        // A dropdown (multiselect) opens when focused: it is only scrolled to, not focused.
        const control = [...field.querySelectorAll("input, select, button")].find((el) => !el.closest(".multiselect"));
        if (control && control.focus) control.focus({ preventScroll: true });
      });
    },

    next() {
      if (this.readOnlyMode) return;
      this.transformLicenses();
      if (!this.stepValid(this.step)) {
        this.showMissingFieldsNotice(this.step);
        return;
      }
      this.showStepError = false;
      if (this.step === 3) {
        this.submit();
      } else {
        this.saveProgress(this.step + 1);
        this.step += 1;
        window.scrollTo(0, 0);
      }
    },

    // Builds the payload sent to /candidate, for both per-step saves and the final submit.
    buildPayload(applicationStep) {
      this.transformLicenses();
      // Re-affirms the declaration date on every save, so it tracks the latest change.
      this.form.sworn_statement_date = this.todayISO();
      return { ...this.payloadValues(), application_step: applicationStep };
    },
    // The form as the backend takes it (without application_step), changing nothing on screen.
    payloadValues() {
      return {
        ...this.form,
        email: this.form.email ? this.form.email.trim() : this.form.email,
        driving_licenses: this.licensesPayload(),
        reference_documents: this.referencesPayload,
        // These are DateField/BooleanField on the backend and reject "".
        cedula_expiration_date: this.form.cedula_expiration_date || null,
        hoja_delincuencia_expiration_date:
          this.form.hoja_delincuencia_expiration_date || null,
        up_to_date_portation: this.form.up_to_date_portation || null,
      };
    },

    // The message for a /candidate save the backend refused because of the email ("" when it wasn't about that).
    emailRejectionMessage(error) {
      const response = error && error.response;
      const emailErrors = response && response.status === 400 && response.data && response.data.email;
      if (!Array.isArray(emailErrors)) return "";
      return emailErrors.includes("email already registered")
        ? "Este correo ya está registrado por otra cuenta."
        : "Correo no válido";
    },
    // True when a /candidate save failed because gov_id is already used by another account.
    isGovIdTakenError(error) {
      return !!(error.response && error.response.data && error.response.data.gov_id_taken);
    },
    showGovIdTakenNotice() {
      this.showNotice({
        variant: "error",
        title: "Número de identificación ya registrado",
        message:
          "No se pudo guardar porque este número de identificación ya está asociado a otra cuenta. Verifique el número ingresado.",
      });
    },

    // Best-effort, non-blocking save of progress after each completed step.
    saveProgress(applicationStep) {
      return candidateApi.saveCandidate(this.buildPayload(applicationStep)).catch((error) => {
        if (this.isGovIdTakenError(error)) {
          this.showGovIdTakenNotice();
          return;
        }
        this.showNotice({
          variant: "error",
          title: "No se pudo guardar su progreso",
          message:
            "No se pudo guardar el progreso de este paso. Puede continuar, pero verifique su conexión.",
        });
      });
    },
    back() {
      this.showStepError = false;
      this.step = Math.max(0, this.step - 1);
      window.scrollTo(0, 0);
    },

    goToStep(idx) {
      if (this.done || idx === this.step) return;

      if (idx < this.step) {
        this.showStepError = false;
        this.step = idx;
        window.scrollTo(0, 0);
        return;
      }

      this.transformLicenses();
      let cursor = this.step;
      while (cursor < idx) {
        if (!this.stepValid(cursor)) {
          this.step = cursor;
          this.showMissingFieldsNotice(cursor);
          window.scrollTo(0, 0);
          return;
        }
        cursor += 1;
      }
      this.showStepError = false;
      this.step = idx;
      window.scrollTo(0, 0);
    },

    // Resolves true once submitted, false if it failed (the applicant has already been told why).
    submit() {
      this.submitting = true;

      const signaturePad = this.$refs.signaturePad;
      return (signaturePad ? signaturePad.captureBlob() : Promise.resolve(null))
        .then((blob) => (blob ? this.saveSignatureBlob(blob) : null))
        .then(() => candidateApi.saveCandidate(this.buildPayload(4)))
        .then(() => {
          // The portal's section editor confirms on its own, without the success panel.
          if (!this.isSectionEditor) {
            this.done = true;
            this.$emit("submitted");
          }
          return true;
        })
        .catch((error) => {
          if (this.isGovIdTakenError(error)) {
            this.showGovIdTakenNotice();
            return false;
          }
          this.showNotice({
            variant: "error",
            title: "No se pudo enviar la solicitud",
            message: "Ocurrió un error al enviar la solicitud. Por favor intente de nuevo.",
          });
          return false;
        })
        .finally(() => {
          this.submitting = false;
        });
    },

    // Portal section editor: validates and saves only the step it shows (the whole candidate is
    // resent, like every wizard save). Resolves true once saved, false if something is missing or
    // the save failed — either way the applicant has already been told why.
    saveSection() {
      if (this.submitting) return Promise.resolve(false);
      this.transformLicenses();
      if (!this.stepValid(this.step)) {
        this.showMissingFieldsNotice(this.step);
        return Promise.resolve(false);
      }
      this.showStepError = false;
      const savedStep = (this.initialData && this.initialData.application_step) || 0;
      if (this.step === 3) {
        // Sending it (application_step 4) needs every earlier step valid, as the wizard guarantees.
        const missing = savedStep < 4 ? this.earlierStepsMissingFields() : [];
        if (missing.length) {
          this.showNotice({
            variant: "warning",
            title: "Faltan datos en su expediente",
            message: "Antes de enviar su solicitud, complete lo siguiente:",
            items: missing,
          });
          return Promise.resolve(false);
        }
        return this.submit();
      }
      this.submitting = true;
      // A different email than the account's: once saved, the account has it (see "email-changed").
      const newEmail = (this.form.email || "").trim();
      const emailChanged = newEmail !== "" && newEmail.toLowerCase() !== (this.userEmail || "").trim().toLowerCase();
      return candidateApi
        .saveCandidate(this.buildPayload(Math.max(savedStep, this.step + 1)))
        .then(() => {
          // Before the portal reloads the candidate, so the editor starts over from the new email.
          if (emailChanged) this.$emit("email-changed", newEmail);
          return true;
        })
        .catch((error) => {
          const emailMessage = this.emailRejectionMessage(error);
          if (emailMessage) {
            // Pointed out on the email field, like any other missing or invalid one.
            this.emailRejection = { email: newEmail.toLowerCase(), message: emailMessage };
            this.showMissingFieldsNotice(this.step);
          } else if (this.isGovIdTakenError(error)) {
            this.showGovIdTakenNotice();
          } else if (error.response && error.response.status === 429) {
            // The backend limits how many times the email can be changed per hour: its answers tell which addresses are
            // registered. The same address can be sent again after waiting, so it isn't marked on the field.
            this.showNotice({
              variant: "warning",
              title: "Demasiados intentos",
              message: "Intentó cambiar el correo demasiadas veces. Espere un momento e intente de nuevo.",
            });
          } else {
            this.showNotice({
              variant: "error",
              title: "No se pudieron guardar los cambios",
              message: "Ocurrió un error al guardar. Verifique su conexión e intente de nuevo.",
            });
          }
          return false;
        })
        .finally(() => {
          this.submitting = false;
        });
    },
    earlierStepsMissingFields() {
      const missing = [];
      for (let s = 0; s < this.step; s += 1) {
        this.stepMissingFields(s).forEach((label) => missing.push(`${this.stepMeta[s]}: ${label}`));
      }
      return missing;
    },
    // Everything the section editor lets the applicant change, serialized.
    editableStateKey() {
      return JSON.stringify({
        form: this.form,
        licenses: this.licenseEntries.map(({ license_id, file_url, expiration_date }) => ({
          license_id,
          file_url,
          expiration_date,
        })),
        reference_documents: this.referencesPayload,
        signing: this.hasSignatureDrawing,
      });
    },
    hasUnsavedChanges() {
      return this.editableStateKey() !== this.initialStateKey;
    },

    // ---- autosave (a section already completed, in the portal) ----

    // Something changed: its save waits for a pause in the typing (the email, a longer one), or goes right away.
    onAutosaveValues(values) {
      if (!this.autosaveSaved) return;
      const seen = this.autosaveSeen || {};
      const changed = Object.keys(values).filter((key) => values[key] !== seen[key]);
      this.autosaveSeen = { ...values };
      if (!changed.length) return;
      if (changed.includes("email")) {
        this.autosaveEmailDue = false;
        clearTimeout(this.autosaveEmailTimer);
        this.autosaveEmailTimer = setTimeout(() => {
          this.autosaveEmailDue = true;
          this.runAutosave();
        }, AUTOSAVE_EMAIL_DELAY);
      }
      const others = changed.filter((key) => key !== "email");
      if (!others.length) return;
      clearTimeout(this.autosaveTimer);
      const typed = others.some((key) => AUTOSAVE_TYPED_KEYS.has(key));
      this.autosaveTimer = setTimeout(() => this.runAutosave(), typed ? AUTOSAVE_TYPING_DELAY : AUTOSAVE_CHOICE_DELAY);
    },
    // What differs from what was saved, as the backend takes it. The email only once its wait is over, and not while
    // it is the one the backend refused.
    autosaveChanges() {
      const values = this.autosaveValues || {};
      const changes = {};
      Object.keys(values).forEach((key) => {
        if (values[key] === this.autosaveSaved[key]) return;
        if (key === "email" && (!this.autosaveEmailDue || this.emailRefused())) return;
        changes[key] = JSON.parse(values[key]);
      });
      return changes;
    },
    emailRefused() {
      const email = String(this.form.email || "").trim().toLowerCase();
      return !!this.emailRejection && this.emailRejection.email === email;
    },
    // Saves what changed, one save at a time. Resolves true once saved (or with nothing to save), false otherwise
    // (the applicant has been told why).
    runAutosave() {
      if (!this.autosaveSaved) return Promise.resolve(true);
      if (this.autosaveInFlight) {
        this.autosaveAgain = true;
        return this.autosaveInFlight;
      }
      // A file still uploading: it goes once the upload is done (see the "busy" watcher).
      if (this.busy) return Promise.resolve(false);
      const changes = this.autosaveChanges();
      const keys = Object.keys(changes);
      if (!keys.length) return Promise.resolve(true);
      // The backend validates the whole section: until it's complete nothing is sent, and what is missing is shown.
      // The email being typed doesn't hold back the rest while it isn't being sent.
      const missing = this.stepMissingFields(this.step).filter((label) => keys.includes("email") || label !== "Correo electrónico");
      if (missing.length) {
        this.reportAutosaveBlocked(missing);
        return Promise.resolve(false);
      }
      this.autosaveBlocked = "";
      const attempt = JSON.stringify(changes);
      if (attempt === this.autosaveRejected) return Promise.resolve(false);
      clearTimeout(this.autosaveRetryTimer);
      this.autosaveInFlight = candidateApi
        .patchCandidateSection(AUTOSAVE_SECTIONS[this.step], changes)
        .then((response) => {
          keys.forEach((key) => this.$set(this.autosaveSaved, key, JSON.stringify(changes[key])));
          this.autosaveRejected = "";
          this.autosaveFailing = false;
          if (keys.includes("email")) {
            this.autosaveEmailDue = false;
            this.$emit("email-changed", changes.email);
          }
          this.$emit("autosaved", { candidate: response.data, labels: this.autosaveLabels(keys) });
          return true;
        })
        .catch((error) => this.onAutosaveError(error, changes, attempt))
        .finally(() => {
          this.autosaveInFlight = null;
          if (this.autosaveAgain) {
            this.autosaveAgain = false;
            this.runAutosave();
          }
        });
      return this.autosaveInFlight;
    },
    // Marks what is missing (also fields the applicant didn't touch) and says so, once for each set of missing fields.
    reportAutosaveBlocked(missing) {
      this.showStepError = true;
      const key = missing.join("|");
      if (key === this.autosaveBlocked) return;
      this.autosaveBlocked = key;
      this.$emit("autosave-blocked", missing);
    },
    onAutosaveError(error, changes, attempt) {
      const status = error && error.response ? error.response.status : 0;
      const emailMessage = "email" in changes ? this.emailRejectionMessage(error) : "";
      if (emailMessage) {
        // Pointed out on the email field; it isn't sent again until it is changed.
        this.emailRejection = { email: String(changes.email || "").toLowerCase(), message: emailMessage };
        this.$set(this.touched, "email", true);
        this.autosaveEmailDue = false;
        this.$emit("autosave-failed", `No se guardó el correo: ${emailMessage}`);
        return false;
      }
      if (status === 400) {
        // The backend refused it as it is: not sent again until something changes.
        this.autosaveRejected = attempt;
        this.$emit("autosave-failed", "No se pudieron guardar los cambios. Revise los datos marcados e intente de nuevo.");
        return false;
      }
      if (status === 429 && "email" in changes) {
        // The email can only be changed so many times per hour: it waits until it is changed again.
        this.autosaveEmailDue = false;
        this.$emit("autosave-failed", "Intentó cambiar el correo demasiadas veces. Espere un momento e intente de nuevo.");
        return false;
      }
      // No connection, the server failing or too many saves in a row: tried again in a moment, telling it once.
      const retryAfter = status === 429 ? Number(error.response.headers && error.response.headers["retry-after"]) : 0;
      clearTimeout(this.autosaveRetryTimer);
      this.autosaveRetryTimer = setTimeout(() => this.runAutosave(), retryAfter > 0 ? retryAfter * 1000 : AUTOSAVE_RETRY_DELAY);
      if (!this.autosaveFailing) {
        this.autosaveFailing = true;
        this.$emit("autosave-failed", "No se pudieron guardar los cambios. Lo intentaremos de nuevo en unos segundos.");
      }
      return false;
    },
    // How the saved changes are named in the portal's notice ("Celular (llamadas)", "Hoja de delincuencia"...).
    autosaveLabels(keys) {
      const fields = [...this.dpFields, this.provinceDef, this.cantonDef, this.districtDef, this.addressDef];
      const labels = [];
      keys.forEach((key) => {
        const field = fields.find((f) => this.fieldKeyFor(f) === key);
        const doc = this.securityDocs.find((d) => [d.key, d.dateKey, d.photoKey].includes(key));
        const label = field ? field.label : doc ? doc.label : AUTOSAVE_LABELS[key] || key;
        if (!labels.includes(label)) labels.push(label);
      });
      return labels;
    },
    // Saves now whatever is waiting (leaving the section, the page going away). Resolves true when nothing is left
    // unsaved.
    flushAutosave() {
      if (!this.autosaveSaved) return Promise.resolve(true);
      clearTimeout(this.autosaveTimer);
      clearTimeout(this.autosaveEmailTimer);
      clearTimeout(this.autosaveRetryTimer);
      this.autosaveEmailDue = true;
      return Promise.resolve(this.autosaveInFlight)
        .then(() => this.runAutosave())
        .then(() => Promise.resolve(this.autosaveInFlight))
        .then(() => !this.dirty);
    },
    onAutosaveUnload(event) {
      if (!this.dirty) return;
      this.flushAutosave();
      event.preventDefault();
      event.returnValue = "";
    },
    // The applicant switches to another app or tab (on a phone, maybe for good): what is waiting is saved now.
    onAutosaveVisibility() {
      if (document.visibilityState === "hidden" && this.dirty) this.flushAutosave();
    },
  },
};
</script>

<style scoped>
.wizard-root {
  width: 100%;
  background: #fff;
  /* At least one screen tall, so the footer sits at the bottom even when a step is shorter than the screen. */
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.wizard-body {
  flex: 1 0 auto;
  width: 100%;
  max-width: 1250px;
  margin: 0 auto;
  padding: 24px clamp(20px, 4vw, 64px) 8px;
}

/* Portal section editor: just the step's fields, inside the portal's editor modal/side panel (which titles it). */
.wizard-root.section-editor {
  min-height: 0;
  background: transparent;
}
.section-editor .wizard-body {
  max-width: none;
  padding: 0;
}
.section-editor .step-title:first-child {
  display: none;
}

/* ---- Datos personales (step 0), edited in place in the portal: the section summary's look ---- */
.step-panel.pf-step {
  padding-bottom: 0;
  font-family: "Geist", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.pf-group {
  padding: 20px 0;
  border-top: 1px solid #eceef3;
}
.pf-group:first-child {
  padding-top: 0;
  border-top: none;
}
.pf-group:last-child {
  padding-bottom: 0;
}
.pf-group-title {
  margin: 0 0 14px;
  font: 500 11px "Geist Mono", ui-monospace, monospace;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #8a91a3;
}
.pf-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(190px, 1fr));
  gap: 18px 20px;
}
.pf-row-start {
  grid-column-start: 1;
}
.pf-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font: 400 12px "Geist", system-ui, sans-serif;
  color: #8a91a3;
}
.pf-error {
  margin-top: 6px;
  font: 500 12px/1.4 "Geist", system-ui, sans-serif;
  color: #c42b2b;
}
.pf-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 14px;
}
.pf-btn {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: 36px;
  margin: 0;
  padding: 0 14px;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
  white-space: nowrap;
  cursor: pointer;
}
.pf-btn:hover:not(:disabled) {
  background: #f4f5f8;
}
.pf-btn:disabled {
  opacity: 0.55;
  cursor: not-allowed;
}
/* Next to an input (the cédula's "Buscar"): as tall as it. */
.pf-btn.pf-btn-input {
  height: 44px;
}
.pf-btn-link {
  padding: 0;
  border: 0;
  background: none;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #6b7283;
  cursor: pointer;
}
.pf-btn-link:hover {
  color: #c42b2b;
}

/* Photo: like the credential's, with its buttons beside it. */
.pf-photo {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 22px;
}
/* The photo beside its title and caption; the upload buttons go under the row, from the left edge. */
.pf-photo-head {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
}
.pf-photo .pf-error {
  margin-top: 0;
}
/* Alone in its group (the name and the identification are on the credential). */
.pf-photo:last-child {
  margin-bottom: 0;
}
.pf-photo-frame {
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: #1a2338;
  color: #8c96ad;
  box-shadow: 0 4px 10px -4px rgba(10, 16, 32, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}
/* A file is being dragged over the photo (v-drag-over): it grows to show it will take the drop. */
.pf-photo-frame.drag-over {
  transform: scale(1.12);
  box-shadow: 0 0 0 2px #e6b65c, 0 6px 14px -6px rgba(183, 131, 42, 0.5);
}
.pf-photo-frame.uploading {
  opacity: 0.6;
}
.pf-photo.pf-invalid .pf-photo-frame {
  box-shadow: 0 0 0 2px #f2c0c0, 0 4px 10px -4px rgba(10, 16, 32, 0.45);
}
.pf-photo-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.pf-photo-frame input[type="file"] {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
}
.pf-photo-frame.uploading input[type="file"] {
  cursor: wait;
}
.pf-photo-body {
  min-width: 0;
}
.pf-photo-title {
  font: 600 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pf-photo-text {
  margin: 2px 0 0;
  font: 400 13px/1.45 "Geist", system-ui, sans-serif;
  color: #6b7283;
}

/* Positions: the chips of the summary, in a soft box like the other fields. */
.pf-positions {
  grid-column: 1 / -1;
  min-width: 0;
}
.pf-multiselect {
  min-height: 44px;
  font-family: "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pf-multiselect ::v-deep .multiselect__select {
  height: 42px;
}
.pf-multiselect ::v-deep .multiselect__tags {
  min-height: 44px;
  padding: 7px 40px 1px 8px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f4f5f8;
  font-size: 15px;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.pf-multiselect:hover ::v-deep .multiselect__tags {
  background: #eef0f4;
}
.pf-multiselect.multiselect--active ::v-deep .multiselect__tags {
  border-color: #e6b65c;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(230, 182, 92, 0.18);
}
.pf-multiselect.invalid ::v-deep .multiselect__tags {
  border-color: #f2c0c0;
  background: #fff6f6;
}
.pf-multiselect ::v-deep .multiselect__placeholder {
  margin-bottom: 6px;
  padding: 3px 0 0 6px;
  font-size: 15px;
  color: #a7adbb;
}
.pf-multiselect ::v-deep .multiselect__input,
.pf-multiselect ::v-deep .multiselect__single {
  margin-bottom: 6px;
  padding: 3px 0 0 6px;
  background: transparent;
  font-size: 15px;
}
.pf-multiselect ::v-deep .multiselect__tag {
  margin: 0 6px 6px 0;
  padding: 6px 28px 6px 12px;
  border-radius: 999px;
  background: #fff;
  box-shadow: inset 0 0 0 1px #e3e6ed;
  font: 500 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pf-multiselect ::v-deep .multiselect__tag-icon {
  border-radius: 0 999px 999px 0;
}
.pf-multiselect ::v-deep .multiselect__tag-icon::after {
  color: #8a91a3;
}
.pf-multiselect ::v-deep .multiselect__tag-icon:hover {
  background: #eceef3;
}
.pf-multiselect ::v-deep .multiselect__tag-icon:hover::after {
  color: #0e1424;
}
.pf-multiselect ::v-deep .multiselect__content-wrapper {
  border-color: #e3e6ed;
  border-radius: 0 0 12px 12px;
  box-shadow: 0 14px 28px -12px rgba(10, 16, 32, 0.25);
}
.pf-multiselect ::v-deep .multiselect__option {
  font-size: 14px;
}
.pf-multiselect ::v-deep .multiselect__option--highlight,
.pf-multiselect ::v-deep .multiselect__option--highlight::after {
  background: #0e1424;
  color: #fff;
}
.pf-multiselect ::v-deep .multiselect__option--selected {
  background: #fff7e8;
  color: #0e1424;
  font-weight: 600;
}
.pf-multiselect ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #24304d;
  color: #fff;
}

/* CV: the summary's file row, with its buttons. */
.pf-file {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid #eceef3;
  border-radius: 14px;
  transition: border-color 0.15s, background-color 0.15s;
}
.pf-file.drag-over {
  border-color: #e6b65c;
  background: #fffbf3;
}
.pf-file.pf-invalid {
  border-color: #f2c0c0;
  background: #fff8f8;
}
.pf-file-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f4f5f8;
  box-shadow: inset 0 0 0 1px #e6e8ee;
  color: #6b7283;
}
.pf-file-body {
  flex: 1;
  min-width: 160px;
}
.pf-file-name {
  font: 500 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pf-file-hint {
  margin-top: 2px;
  font: 400 12px "Geist", system-ui, sans-serif;
  color: #9aa1b1;
}
.pf-file-input {
  display: none;
}

/* ---- Residencia (step 2), edited in place: the summary's address, map and croquis, each with its fields ---- */
.pr-grid {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}
.pr-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 4px 12px;
}
.pr-head .pf-group-title {
  margin-bottom: 8px;
}
.pr-links {
  display: flex;
  align-items: center;
  gap: 14px;
}
.pr-maps-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
  text-decoration: underline;
  text-underline-offset: 3px;
}
.pr-maps-link:hover {
  color: #b7832a;
}
.pr-hint {
  margin: 0 0 12px;
  font: 400 13px/1.45 "Geist", system-ui, sans-serif;
  color: #6b7283;
}
.pr-error {
  margin: 0 0 10px;
}
.pr-map {
  position: relative;
  overflow: hidden;
  border: 1px solid #eceef3;
  border-radius: 16px;
}
.pr-map.pf-invalid {
  border-color: #f2c0c0;
  box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.08);
}
.pr-gmap {
  width: 100%;
  height: 340px;
}
/* The address search floats over the map's top-left corner. */
.pr-search {
  position: absolute;
  top: 12px;
  left: 12px;
  right: 56px;
  z-index: 1;
  box-sizing: border-box;
  max-width: 320px;
  height: 40px;
  padding: 0 14px;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 6px 16px -8px rgba(10, 16, 32, 0.35);
  font: 500 14px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pr-search:focus {
  outline: none;
  border-color: #e6b65c;
  box-shadow: 0 0 0 3px rgba(230, 182, 92, 0.18);
}

/* ---- Declaración (step 3), in the portal's DeclarationModal ---- */
.pg-text {
  padding: 16px 18px;
  border: 1px solid #eceef3;
  border-radius: 16px;
  background: #fafbfc;
  font: 400 14px/1.65 "Geist", system-ui, sans-serif;
  color: #3d4558;
}
.pg-text p {
  margin: 0 0 10px;
}
.pg-text p:last-child {
  margin-bottom: 0;
}
.pg-text strong {
  font-weight: 600;
  color: #0e1424;
}
.pg-accept {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin: 14px 0 0;
  padding: 14px 16px;
  border: 1px solid #eceef3;
  border-radius: 14px;
  background: #fff;
  font: 500 14px/1.45 "Geist", system-ui, sans-serif;
  color: #0e1424;
  cursor: pointer;
  transition: background-color 0.15s, border-color 0.15s;
}
.pg-accept:hover {
  background: #fafbfc;
}
.pg-accept input {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  margin: 1px 0 0;
  accent-color: #0e1424;
  cursor: pointer;
}
.pg-accept.pf-invalid {
  border-color: #f2c0c0;
  background: #fff8f8;
}
.pg-step .pf-error {
  margin-top: 8px;
}
.pg-date {
  max-width: 260px;
  margin-top: 16px;
  grid-template-columns: 1fr;
}
.pg-sign {
  max-width: 480px;
}

@media (max-width: 640px) {
  .pr-grid {
    grid-template-columns: 1fr;
  }
  .pr-gmap {
    height: 300px;
  }
  .pr-search {
    max-width: none;
  }
}

/* ---- Documentación (step 1), edited in place: the section summary's document list, each row with its controls ---- */
.step-panel.pd-step {
  padding-bottom: 0;
  font-family: "Geist", system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}
.pd-group + .pd-group {
  margin-top: 22px;
}
.pd-step .pf-group-title {
  margin-bottom: 10px;
}
/* Every document in a bubble of its own. */
.pd-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin: 0;
  padding: 0;
  list-style: none;
}
.pd-item {
  position: relative;
  padding: 12px 16px;
  border: 1px solid #eceef3;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.04);
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.pd-item:hover {
  border-color: #e3e6ed;
  background: #fafbfc;
}
.pd-item.pf-invalid {
  border-color: #f2c0c0;
  background: #fffafa;
}
/* Icon | name, with the badge and the details under it | the controls, on the right. */
.pd-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px 14px;
}
.pd-icon {
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pd-icon.tone-success {
  background: #eaf8f1;
  color: #0f7a4f;
  box-shadow: inset 0 0 0 1px #bfe8d3;
}
.pd-icon.tone-warning {
  background: #fff1e2;
  color: #b4540a;
  box-shadow: inset 0 0 0 1px #fad9b0;
}
.pd-icon.tone-danger {
  background: #fdecec;
  color: #c42b2b;
  box-shadow: inset 0 0 0 1px #f6c9c9;
}
.pd-icon.tone-neutral {
  background: #f4f5f8;
  color: #9aa1b1;
  box-shadow: inset 0 0 0 1px #e6e8ee;
}
.pd-main {
  flex: 1 1 220px;
  min-width: 0;
}
.pd-name {
  font: 500 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pd-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  margin-top: 3px;
  font: 400 13px "Geist", system-ui, sans-serif;
  color: #6b7283;
}
/* The status badge, at the start of the details line: a notch smaller than the summary's. */
.pd-meta .status-badge {
  min-width: 0;
  height: 22px;
  padding: 0 8px;
  font-size: 11.5px;
}
.pd-controls {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  margin-left: auto;
}
/* The "Ver" buttons, as tall as the other controls. */
.pd-controls .file-link {
  height: 34px;
}
/* A date (or the license type) in a soft pill, its label inside. */
.pd-date {
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  margin: 0;
  padding: 0 6px 0 11px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f4f5f8;
  font: 400 12px "Geist", system-ui, sans-serif;
  color: #8a91a3;
  white-space: nowrap;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
.pd-date:hover {
  background: #eef0f4;
}
.pd-date:focus-within {
  border-color: #e6b65c;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(230, 182, 92, 0.18);
}
.pd-date.invalid {
  border-color: #f2c0c0;
  background: #fff6f6;
}
/* Read from the document: shown, not edited. */
.pd-date.locked {
  background: transparent;
}
.pd-date input {
  height: 32px;
  padding: 0 4px;
  border: 0;
  background: transparent;
  font: 600 13.5px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pd-date input:focus {
  outline: none;
}
.pd-date input:disabled {
  color: #0e1424;
  cursor: default;
}
/* The license type: vue-multiselect, dressed as the pill's value. Its list hangs from the whole pill. */
.pd-select {
  position: relative;
  padding-right: 0;
}
.pd-multiselect {
  position: static;
  width: 104px;
  min-height: 32px;
  font-family: "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pd-multiselect ::v-deep .multiselect__tags {
  min-height: 32px;
  padding: 6px 30px 0 4px;
  border: 0;
  background: transparent;
  font-size: 13.5px;
}
.pd-multiselect ::v-deep .multiselect__single,
.pd-multiselect ::v-deep .multiselect__placeholder {
  display: block;
  margin: 0;
  padding: 0;
  background: transparent;
  font-size: 13.5px;
  line-height: 20px;
}
.pd-multiselect ::v-deep .multiselect__single {
  font-weight: 600;
}
.pd-multiselect ::v-deep .multiselect__placeholder {
  color: #a7adbb;
}
.pd-multiselect ::v-deep .multiselect__select {
  top: 0;
  right: 0;
  width: 30px;
  height: 32px;
  padding: 0;
}
.pd-multiselect ::v-deep .multiselect__select::before {
  top: 55%;
  border-color: #8a91a3 transparent transparent;
}
.pd-multiselect ::v-deep .multiselect__content-wrapper {
  left: 0;
  min-width: 170px;
  margin-top: 6px;
  border: 1px solid #e3e6ed;
  border-radius: 12px;
  box-shadow: 0 14px 28px -12px rgba(10, 16, 32, 0.25);
}
/* Roomy rows: easy to hit with a finger. */
.pd-multiselect ::v-deep .multiselect__option {
  min-height: 40px;
  padding: 11px 14px;
  font: 600 14px/18px "Geist", system-ui, sans-serif;
  color: #0e1424;
}
.pd-multiselect ::v-deep .multiselect__option--group {
  min-height: 0;
  padding: 10px 14px 4px;
  background: #fff;
  font: 500 11px "Geist Mono", ui-monospace, monospace;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #8a91a3;
}
.pd-multiselect ::v-deep .multiselect__option--highlight {
  background: #f4f5f8;
  color: #0e1424;
}
.pd-multiselect ::v-deep .multiselect__option--selected {
  background: #fff7e8;
  color: #0e1424;
}
.pd-multiselect ::v-deep .multiselect__option--selected.multiselect__option--highlight {
  background: #fdebc8;
}
.pd-multiselect ::v-deep .multiselect__option::after {
  display: none;
}
/* Sí / No as a two-way switch: a bordered track, with the chosen side lifted in white. */
.pd-segmented {
  box-sizing: border-box;
  display: inline-flex;
  gap: 3px;
  height: 34px;
  padding: 3px;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #eef0f4;
}
.pd-segmented.invalid {
  border-color: #f2c0c0;
  background: #fff6f6;
}
.pd-segment {
  min-width: 48px;
  height: 100%;
  padding: 0 12px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #4a5266;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.pd-segmented.invalid .pd-segment:not(.selected) {
  color: #c42b2b;
}
.pd-segment:hover:not(.selected) {
  background: rgba(255, 255, 255, 0.7);
  color: #0e1424;
}
.pd-segment.selected {
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.08), 0 0 0 1px #d9dde6;
  color: #0e1424;
}
.pd-segment:focus {
  outline: none;
}
.pd-segment:focus-visible {
  box-shadow: 0 0 0 2px #e6b65c;
}
/* Removes a license or a reference. */
.pd-remove {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  padding: 0;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: #8a91a3;
  cursor: pointer;
}
.pd-remove:hover {
  background: #fdecec;
  color: #c42b2b;
}
.pd-errors {
  margin: 8px 0 0;
  padding: 0 0 0 50px;
  list-style: none;
  font: 500 12px/1.5 "Geist", system-ui, sans-serif;
  color: #c42b2b;
}
.pd-empty {
  margin: 0;
  padding: 12px 16px;
  border: 1px dashed #e3e6ed;
  border-radius: 14px;
  background: #fafbfc;
  font: 400 14px "Geist", system-ui, sans-serif;
  color: #6b7283;
}
.pd-add {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 34px;
  margin-top: 10px;
  padding: 0 14px;
  border: 1px solid #e3e6ed;
  border-radius: 10px;
  background: #fff;
  font: 600 13px "Geist", system-ui, sans-serif;
  color: #0e1424;
  cursor: pointer;
}
.pd-add:hover {
  background: #f4f5f8;
}
.pd-add span {
  font-size: 16px;
  line-height: 1;
}

/* Phones: the controls drop under the name, each across the full width ("Ver" on top, the cédula's two uploads
   half and half, the date last); the × that removes a license or a reference goes to the row's corner. */
@media (max-width: 640px) {
  .pd-main {
    box-sizing: border-box;
    flex-basis: calc(100% - 50px);
  }
  .pd-removable .pd-main {
    padding-right: 34px;
  }
  .pd-controls {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    width: 100%;
    margin-left: 0;
  }
  .pd-controls > * {
    grid-column: 1 / -1;
    min-width: 0;
  }
  .pd-controls > .pd-half {
    grid-column: auto;
  }
  .pd-controls .file-link {
    width: 100%;
    justify-content: center;
  }
  .pd-controls .upload,
  .pd-controls .pd-date,
  .pd-controls .pd-segmented {
    display: flex;
    width: 100%;
  }
  .pd-date input {
    flex: 1;
    min-width: 0;
  }
  .pd-multiselect {
    flex: 1;
    width: auto;
    min-width: 0;
  }
  .pd-segment {
    flex: 1;
  }
  .pd-controls .pd-remove {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 30px;
    height: 30px;
  }
}

.stepper {
  margin-bottom: 20px;
}
.stepper-track {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}
.stepper-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-width: 90px;
}
.stepper-item.clickable {
  cursor: pointer;
}
.stepper-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font: 700 14px "IBM Plex Sans", sans-serif;
  border: 2px solid #d3d0c8;
  background: #fff;
  color: #9aa0ac;
}
.stepper-circle.complete {
  background: #16223d;
  border-color: #16223d;
  color: #fff;
}
.stepper-circle.active {
  background: #c9963a;
  border-color: #c9963a;
  color: #fff;
}
.stepper-label {
  font: 600 11px "IBM Plex Sans", sans-serif;
  text-align: center;
  color: #a3a196;
}
.stepper-label.active {
  color: #16223d;
}
.stepper-progress {
  height: 6px;
  background: #e7e3d8;
  border-radius: 99px;
  overflow: hidden;
  margin-top: 16px;
}
.stepper-progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #c9963a, #e0b862);
  border-radius: 99px;
  transition: width 0.35s;
}

.wizard-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}
.wizard-fieldset[disabled] {
  cursor: not-allowed;
}
.wizard-fieldset[disabled] .step-panel {
  opacity: 0.75;
}
.step-panel {
  padding-bottom: 12px;
}
.step-title {
  margin: 0 0 18px;
  font: 600 18px "IBM Plex Serif", serif;
  color: #16223d;
}
.field-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 16px 18px;
  margin-bottom: 22px;
}
.field-grid.two-col {
  grid-template-columns: 1fr 1fr;
}
.field-grid.three-col {
  grid-template-columns: 1fr 1fr 1fr;
}
.wide-field {
  margin-bottom: 22px;
}

.form-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.field-label {
  font: 600 12.5px "IBM Plex Sans", sans-serif;
  color: #2b3448;
  margin-bottom: 7px;
  display: block;
}
.req {
  color: #b3261e;
  margin-left: 3px;
}
.field-input {
  box-sizing: border-box;
  width: 100%;
  padding: 11px 13px;
  border: 1px solid #d3d0c8;
  border-radius: 9px;
  font: 14px "IBM Plex Sans", sans-serif;
  color: #1c2434;
  background: #fff;
}
.field-input:disabled {
  background: #f3f1ec;
  color: #5b5e68;
  cursor: not-allowed;
}
.field-input:focus {
  outline: none;
  border-color: #c9963a;
  box-shadow: 0 0 0 3px rgba(201, 150, 58, 0.18);
}
.field-input.invalid {
  border-color: #d33a30;
}
.field-select {
  cursor: pointer;
}

.btn-outline {
  font: 600 14px "IBM Plex Sans", sans-serif;
  color: #16223d;
  background: #fff;
  border: 1px solid #c9c6bc;
  padding: 11px 20px;
  border-radius: 9px;
  cursor: pointer;
}
.btn-outline.small {
  padding: 0 14px;
  white-space: nowrap;
}
.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.btn-gold {
  font: 600 14px "IBM Plex Sans", sans-serif;
  padding: 11px 24px;
  border-radius: 9px;
  cursor: pointer;
  border: none;
  background: linear-gradient(90deg, #e0b862, #c9963a);
  color: #16223d;
  box-shadow: 0 6px 16px -8px rgba(201, 150, 58, 0.9);
}
.btn-gold:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.field-error {
  font: 12px "IBM Plex Sans", sans-serif;
  color: #b3261e;
  margin-top: 8px;
}

.doc-row-error {
  /* Cancels .field-error's margin-top. */
  margin-top: 0;
}

/* Pinned to the bottom of the screen while the form scrolls underneath it. */
.wizard-footer {
  position: sticky;
  bottom: 0;
  z-index: 10;
  background: #fff;
  border-top: 1px solid #d3d0c8;
}
/* Keeps the buttons lined up with the form column while the bar spans the full width. */
.wizard-footer-inner {
  max-width: 1250px;
  margin: 0 auto;
  padding: 16px clamp(20px, 4vw, 64px);
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}
.step-count {
  font: 500 12px "IBM Plex Sans", sans-serif;
  color: #8a8578;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(22, 34, 61, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  z-index: 1000;
}
.modal-card {
  position: relative;
  background: #fff;
  border-radius: 14px;
  padding: 28px 26px;
  max-width: 420px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 50px -12px rgba(22, 34, 61, 0.45);
}
.modal-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 30px;
  height: 30px;
  border: none;
  background: none;
  color: #8a8f9c;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  border-radius: 50%;
}
.modal-close:hover {
  background: #f3f1ec;
  color: #16223d;
}
.modal-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  background: #fbf3e4;
  color: #8a5a00;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Plain ASCII letter, not a Unicode symbol (ℹ/⚠) — renders consistently. */
  font: 700 22px "IBM Plex Sans", sans-serif;
  margin: 0 auto 14px;
}
.modal-card-info .modal-icon {
  background: #e9f3ec;
  color: #215c3d;
}
.modal-card-error .modal-icon {
  background: #fbecea;
  color: #9a2820;
}
.modal-title {
  font: 600 18px "IBM Plex Sans", sans-serif;
  color: #16223d;
  margin-bottom: 12px;
}
.modal-text {
  font: 400 13.5px/1.6 "IBM Plex Sans", sans-serif;
  color: #3f434e;
  margin: 0 0 12px;
}
.modal-list {
  text-align: left;
  margin: 0 0 16px;
  padding-left: 20px;
  font: 500 13px "IBM Plex Sans", sans-serif;
  color: #dc2626;
}
.modal-list li {
  margin-bottom: 5px;
}
.modal-card .btn-gold {
  margin-top: 8px;
}

@media (max-width: 720px) {
  .stepper-item {
    min-width: 70px;
  }
  .stepper-label {
    font-size: 10px;
  }
}

@media (max-width: 525px) {
  .field-grid,
  .field-grid.two-col,
  .field-grid.three-col {
    grid-template-columns: 1fr;
  }
  .form-field.span-2 {
    grid-column: span 1;
  }
  .wizard-footer .btn-outline,
  .wizard-footer .btn-gold {
    font-size: 13px;
    padding: 9px 16px;
  }
}
</style>
